/**
 * theme.js — faith template
 * Sections:
 *  1. Constants & Events
 *  2. Pub/Sub
 *  3. Utilities
 *  4. Accessibility helpers (focus trap, details handling)
 *  5. Shopify core helpers & money formatter
 *  6. Performance tracer (CartTracker)
 *  7. Small UI helpers (hover text) 
 *  8. Components (customElements)
 */



/* ==========================================================================
   1. Constants & Events
   ========================================================================== */

const ON_CHANGE_DEBOUNCE_TIMER = 300;

const EVENT_TYPES = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/* ==========================================================================
   2. Pub/Sub (simple)
   ========================================================================== */

let subscribers = {};

const subscribe = (eventName, callback) => {
  if (!subscribers[eventName]) subscribers[eventName] = [];
  subscribers[eventName].push(callback);
  return () => {
    subscribers[eventName] = subscribers[eventName].filter(cb => cb !== callback);
  };
};

const publish = (eventName, data) => {
  if (!subscribers[eventName]) return Promise.resolve();
  try {
    const promises = subscribers[eventName].map(cb => cb(data));
    return Promise.all(promises);
  } catch (err) {
    return Promise.reject(err);
  }
};


/* ==========================================================================
   3. Utilities
   ========================================================================== */

const getFocusableElements = container => Array.from(
  container.querySelectorAll(
    "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
  )
);

const SectionId = class {
  static #separator = '__';
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
};

const HTMLUpdate = class {
  static viewTransition(oldNode, newContent, preCallbacks = [], postCallbacks = []) {
    preCallbacks?.forEach(cb => cb(newContent));
    const wrapper = document.createElement('div');
    HTMLUpdate.setInnerHTML(wrapper, newContent.outerHTML);
    const newNode = wrapper.firstChild;

    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach(el => {
      if (el.id) el.id = `${el.id}-${uniqueKey}`;
      if (el.form) el.setAttribute('form', `${el.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postCallbacks?.forEach(cb => cb(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  static setInnerHTML(el, html) {
    el.innerHTML = html;
    // Re-execute scripts
    el.querySelectorAll('script').forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }
};

window.pauseAllMedia = (element = document) => {
  element.querySelectorAll('.js-youtube').forEach(video => {
    try {
      video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    } catch (e) { }
  });

  element.querySelectorAll('.js-vimeo').forEach(video => {
    try {
      video.contentWindow.postMessage('{"method":"pause"}', '*');
    } catch (e) { }
  });

  element.querySelectorAll('video').forEach(video => video.pause());

  element.querySelectorAll('product-model').forEach(model => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });

  element.querySelectorAll('.video-play-pause-button').forEach(trigger => {
    if (trigger != null) {
      trigger.dataset.playing = 'false';
    }
  });

};

const resumeMedia = (element = document) => {
  element.querySelectorAll('.js-youtube, .js-vimeo, video').forEach(video => {
    if (!isInViewport(video)) return;

    const postMessage =
      video.classList.contains('js-youtube') && video.tagName !== 'VIDEO'
        ? '{"event":"command","func":"playVideo","args":""}'
        : video.classList.contains('js-vimeo') && video.tagName !== 'VIDEO'
          ? '{"method":"play"}'
          : null;

    if (postMessage) {
      try { video.contentWindow.postMessage(postMessage, '*'); } catch (e) { }
    }

    if (video.tagName === 'VIDEO') {
      try { video.play(); } catch (e) { }
    }
  });
};

const serializeForm = form => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) obj[key] = formData.get(key);
  return JSON.stringify(obj);
};

const deepClone = obj => JSON.parse(JSON.stringify(obj));

const handleize = str => (str || '').replace(/[ /_]/g, '-').toLowerCase();

const decode = str => decodeURIComponent((str || '')).replace(/\+/g, ' ');

const getOffsetTop = el => {
  let offsetTop = 0;
  while (el) {
    if (!isNaN(el.offsetTop)) offsetTop += el.offsetTop;
    el = el.offsetParent;
  }
  return offsetTop;
};

const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

const Throttle = (fn, delay) => {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall < delay) return;
    lastCall = now;
    return fn(...args);
  };
};

const fetchConfig = (type = 'json') => ({
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Accept: `application/${type}`,
  },
});

function isInViewport(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return false;
  const rect = el.getBoundingClientRect();
  return rect.top < (window.innerHeight || document.documentElement.clientHeight) && rect.bottom >= 0;
}


/* ==========================================================================
   4. Accessibility helpers (focus trap & details)
   ========================================================================== */

const trapFocusHandlers = {};

const removeTrapFocus = (elementToFocus = null) => {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener(
    'focusout',
    trapFocusHandlers.focusout
  );
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
};

const trapFocus = (container, elementToFocus = container) => {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = event => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener(
      'keydown',
      trapFocusHandlers.keydown
    );
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
};

const OnKeyUpEscape = (event) => {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetails = event.target.closest('details[open]');
  if (!openDetails) return;

  const summaryEl = openDetails.querySelector('summary');
  openDetails.removeAttribute('open');
  summaryEl.setAttribute('aria-expanded', false);
  summaryEl.focus();
};

document.querySelectorAll('[id^="Details-"] summary').forEach(summary => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  const contentEl = summary.nextElementSibling;
  if (contentEl && contentEl.id) summary.setAttribute('aria-controls', contentEl.id);

  summary.addEventListener('click', event => {
    const details = event.currentTarget.closest('details');
    const isOpen = details.hasAttribute('open');
    event.currentTarget.setAttribute('aria-expanded', !isOpen);
  });

  summary.parentElement.addEventListener('keyup', OnKeyUpEscape);
});

/* Initialize trap focus on open details */
document.querySelectorAll('.disclosure-has-popup').forEach(details => {
  details.addEventListener('toggle', () => {
    if (details.hasAttribute('open')) trapFocus.trap(details);
  });
});


/* ==========================================================================
   5. Shopify core helpers & formatMoney
   ========================================================================== */

if (typeof window.Shopify == 'undefined') {
  window.Shopify = window.Shopify || {};
}

Shopify.bind = function (fn, scope) {
  return function () { return fn.apply(scope, arguments); };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};
  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);
  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));
  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },
  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) Shopify.setSelectorByValue(this.provinceEl, value);
  },
  countryHandler: function () {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);
    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }
      this.provinceContainer.style.display = '';
    }
  },
  clearOptions: function (selector) {
    while (selector.firstChild) selector.removeChild(selector.firstChild);
  },
  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < count; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

// Money format handler
Shopify.money_format = "${{amount_no_decimals}}";
Shopify.formatMoney = function (cents, format) {
  if (typeof cents == 'string') cents = cents.replace('.', '');
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = (format || this.money_format);

  function defaultOption(opt, def) {
    return (typeof opt === 'undefined' ? def : opt);
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    if (isNaN(number) || number == null) return 0;
    number = (number / 100.0).toFixed(precision);
    var parts = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      cents = parts[1] ? (decimal + parts[1]) : '';
    return dollars + cents;
  }

  var match = formatString.match(placeholderRegex);
  if (!match) return formatString;

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

/* ==========================================================================
   6. Performance tracer (CartTracker)
   ========================================================================== */

class CartTracker {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartTracker.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartTracker.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartTracker.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartTracker.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

/* ==========================================================================
   7. Small UI helpers
   ========================================================================== */
document.addEventListener("DOMContentLoaded", () => {
  const addHoverText = () => {
    document.querySelectorAll(".button").forEach((button) => {
      if (button.querySelector(".hover--text")) return;
      const normalText = button.querySelector(".button--text");
      if (!normalText) return;
      const hoverText = normalText.cloneNode(true);
      hoverText.classList.replace("button--text", "hover--text");
      button.appendChild(hoverText);
    });
  };
  new MutationObserver(addHoverText).observe(document.body, { childList: true, subtree: true });
  addHoverText();
});



(function () {
  const SELECTOR = 'img[loading="lazy"]';
  let observer;

  function initLazyImages(root = document) {
    const images = root.querySelectorAll(SELECTOR);
    if (!images.length) return;

    if (!observer && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(onIntersect, {
        rootMargin: '200px 0px',
        threshold: 0.01
      });
    }

    images.forEach(img => {
      if (img.dataset.lazyHandled) return;
      img.dataset.lazyHandled = 'true';

      // cached images (very important for editor)
      if (img.complete && img.naturalWidth > 0) {
        markLoaded(img);
        return;
      }

      if (observer) {
        observer.observe(img);
      } else {
        img.addEventListener('load', () => markLoaded(img), { once: true });
      }
    });
  }

  function onIntersect(entries) {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;

      const img = entry.target;
      observer.unobserve(img);

      if (img.complete && img.naturalWidth > 0) {
        markLoaded(img);
      } else {
        img.addEventListener('load', () => markLoaded(img), { once: true });
      }
    });
  }

  function markLoaded(img) {
    const wrapper = img.parentElement;
    if (!wrapper) return;

    img.classList.add('is-loaded');
    wrapper.classList.add('is-loaded');

  }





  /* --------------------
     Public API
  -------------------- */
  window.initLazyImages = initLazyImages;

  /* Normal page load */
  document.addEventListener('DOMContentLoaded', () => initLazyImages());
  window.addEventListener('load', () => initLazyImages());

  /* Resize */
  window.addEventListener(
    'resize',
    debounce(() => initLazyImages(), 150)
  );

  /* --------------------
     ✅ Shopify Theme Editor FIX
  -------------------- */
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', e => {
      initLazyImages(e.target);
    });

    document.addEventListener('shopify:block:select', e => {
      initLazyImages(e.target);
    });

    document.addEventListener('shopify:section:reorder', () => {
      initLazyImages();
    });
  }

  /* --------------------
     MutationObserver fallback
     (covers dynamic DOM swaps)
  -------------------- */
  const mo = new MutationObserver(mutations => {
    for (const m of mutations) {
      if (m.addedNodes.length) {
        initLazyImages();
        break;
      }
    }
  });

  mo.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();




/* ==========================================================================
   8. Components (customElements)
   ========================================================================== */


/* -------------------------
   Base QuantitySelectorComponent
------------------------- */
if (!customElements.get('quantity-selector-component')) {

  class QuantitySelectorComponent extends HTMLElement {
    requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];

    constructor() {
      super();
      this.refs = {};
      this.serverDisabledMinus = false;
      this.serverDisabledPlus = false;
    }

    connectedCallback() {
      this.resolveRefs();

      const { minusButton, plusButton } = this.refs;
      this.serverDisabledMinus = minusButton.disabled;
      this.serverDisabledPlus = plusButton.disabled;

      this.bindEvents();
      this.refreshControls();
    }

    /* -------------------------
       Ref Resolver
    ------------------------- */
    resolveRefs() {
      this.requiredRefs.forEach((name) => {
        const el = this.querySelector(`[ref="${name}"]`);
        if (!el) throw new Error(`Missing ref="${name}" inside <quantity-selector-component>`);
        this.refs[name] = el;
      });
    }

    parseIntOrDefault(value, fallback) {
      const parsed = parseInt(value);
      return isNaN(parsed) ? fallback : parsed;
    }

    readState() {
      const { quantityInput } = this.refs;
      return {
        min: this.parseIntOrDefault(quantityInput.min, 1),
        max: quantityInput.max ? this.parseIntOrDefault(quantityInput.max, null) : null,
        step: this.parseIntOrDefault(quantityInput.step, 1),
        value: this.parseIntOrDefault(quantityInput.value, 0),
        cartQuantity: this.parseIntOrDefault(quantityInput.dataset.cartQuantity, 0),
      };
    }

    calculateLimit() {
      const { max, cartQuantity, min } = this.readState();
      if (max === null) return null;
      return Math.max(max - cartQuantity, min);
    }

    refreshControls() {
      const { minusButton, plusButton } = this.refs;
      const { min, value } = this.readState();
      const limit = this.calculateLimit();

      if (!this.serverDisabledMinus) minusButton.disabled = value <= min;
      if (!this.serverDisabledPlus) plusButton.disabled = limit !== null && value >= limit;
    }

    adjustQuantity(direction) {
      const { quantityInput } = this.refs;
      const { min, step, value } = this.readState();
      const limit = this.calculateLimit();

      const next = Math.min(limit ?? Infinity, Math.max(min, value + step * direction));
      quantityInput.value = next.toString();

      this.refreshControls();
      this.emitChange?.(); // call only if child implements
    }



    bindEvents() {
      const { plusButton, minusButton, quantityInput } = this.refs;

      plusButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.adjustQuantity(1);
      });

      minusButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.adjustQuantity(-1);
      });

      quantityInput.addEventListener('focus', () => quantityInput.select());

      quantityInput.addEventListener('change', () => {
        this.refreshControls();
        this.emitChange?.();
      });
    }
  }

  customElements.define('quantity-selector-component', QuantitySelectorComponent);



  /* -------------------------
    CartQuantitySelectorComponent
 ------------------------- */

  class CartQuantitySelectorComponent extends QuantitySelectorComponent {

    constructor() {
      super();
      this.pendingUpdate = false; // prevents race condition on fast clicks
    }

    emitChange() {
      const { quantityInput, plusButton, minusButton } = this.refs;
      const line = Number(quantityInput.dataset.cartLine);
      const value = Number(quantityInput.value);
      const variantId = Number(quantityInput.dataset.quantityVariantId) || null;

      if (!line || this.pendingUpdate) return;

      this.pendingUpdate = true;

      // disable buttons during update
      plusButton.disabled = true;
      minusButton.disabled = true;

      const cartItems = document.querySelector('cart-items') || document.querySelector('side-cart-items');
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        cartItems.updateQuantity(line, value, { currentTarget: this }, quantityInput.name, variantId)
          .finally(() => {
            this.pendingUpdate = false;
            this.refreshControls(); // re-enable buttons
          });
      } else {
        this.pendingUpdate = false;
        this.refreshControls();
      }
    }

    adjustQuantity(direction) {
      const { quantityInput } = this.refs;
      const { min, step, value } = this.readState();
      const limit = this.calculateLimit();
      const next = Math.min(limit ?? Infinity, Math.max(min, value + step * direction));
      quantityInput.value = next.toString();

      if (!this.pendingUpdate) {
        this.refreshControls();
        this.emitChange?.();
      }
    }
  }


  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);

}


/* -------------------------
   PricePerItemComponent
------------------------- */

class PricePerItemComponent extends HTMLElement {
  constructor() {
    super();
    this.textEl = null;
  }

  connectedCallback() {
    this.textEl = this.querySelector('[ref="pricePerItemText"]');

    this.variantId = this.dataset.variantId;
    this.basePrice = this.dataset.variantPrice;
    this.priceBreaks = JSON.parse(this.dataset.priceBreaks || '[]');
    this.atText = this.dataset.atText || '';
    this.eachText = this.dataset.eachText || '';

    this.bindQuantityListener();
    this.updateFromCurrentQuantity();
  }

  bindQuantityListener() {
    document.addEventListener('change', (e) => {
      const qs = e.target.closest('quantity-selector-component');
      if (!qs) return;

      if (qs.dataset.variantId !== this.variantId) return;

      this.updateFromComponent(qs);
    });

    document.addEventListener('click', (e) => {
      const qs = e.target.closest('quantity-selector-component');
      if (!qs) return;

      if (qs.dataset.variantId !== this.variantId) return;

      if (
        e.target.closest('[name="plus"]') ||
        e.target.closest('[name="minus"]')
      ) {
        setTimeout(() => this.updateFromComponent(qs), 0);
      }
    });
  }

  updateFromCurrentQuantity() {
    const qs = document.querySelector(
      `quantity-selector-component[data-variant-id="${this.variantId}"]`
    );

    if (!qs) return;

    this.updateFromComponent(qs);
  }

  updateFromComponent(qs) {
    const input = qs.querySelector('[ref="quantityInput"]');
    if (!input) return;

    const qty = parseInt(input.value);
    let newPrice = this.basePrice;

    for (const item of this.priceBreaks) {
      if (qty >= item.quantity) {
        newPrice = item.price;
        break;
      }
    }

    this.render(newPrice);
  }

  render(price) {
    if (!this.textEl) return;

    this.textEl.innerHTML =
      this.atText + ' ' + price + ' / ' + this.eachText;
  }
}

customElements.define('price-per-item', PricePerItemComponent);




if (!customElements.get('quantity-control')) {
  class QuantityControl extends HTMLElement {
    constructor() {
      super();
      this.input = null;
      this.minus = null;
      this.plus = null;
      this.min = 1;
      this.max = Number.MAX_SAFE_INTEGER;
      this.onMinus = this.onMinus.bind(this);
      this.onPlus = this.onPlus.bind(this);
      this.onInput = this.onInput.bind(this);
    }
    connectedCallback() {
      this.input = this.querySelector('.product-quantity');
      this.minus = this.querySelector('.decrease-quantity');
      this.plus = this.querySelector('.increase-quantity');
      if (!this.input || !this.minus || !this.plus) return;
      this.min = parseInt(this.input.getAttribute('min')) || 1;
      this.max = parseInt(this.input.getAttribute('max')) || Number.MAX_SAFE_INTEGER;
      this.minus.addEventListener('click', this.onMinus);
      this.plus.addEventListener('click', this.onPlus);
      this.input.addEventListener('change', this.onInput);
      this.input.addEventListener('keyup', this.onInput);
      this.updateDisabled();
    }
    disconnectedCallback() {
      if (this.minus) this.minus.removeEventListener('click', this.onMinus);
      if (this.plus) this.plus.removeEventListener('click', this.onPlus);
      if (this.input) {
        this.input.removeEventListener('change', this.onInput);
        this.input.removeEventListener('keyup', this.onInput);
      }
    }
    clamp(v) {
      const n = isNaN(parseInt(v)) ? this.min : parseInt(v);
      if (n < this.min) return this.min;
      if (n > this.max) return this.max;
      return n;
    }
    setValue(v, notify = true) {
      const next = this.clamp(v);
      this.input.value = String(next);
      this.updateDisabled();
      if (notify) {
        const evt = new Event('change', { bubbles: true });
        this.input.dispatchEvent(evt);
        publish(EVENT_TYPES.quantityUpdate, { el: this, value: next });
      }
    }
    updateDisabled() {
      const v = this.clamp(this.input.value);
      if (this.minus) this.minus.toggleAttribute('disabled', v <= this.min);
      if (this.plus) this.plus.toggleAttribute('disabled', v >= this.max);
    }
    onMinus(e) {
      e.preventDefault();
      this.setValue(this.clamp(this.input.value) - 1);
    }
    onPlus(e) {
      e.preventDefault();
      this.setValue(this.clamp(this.input.value) + 1);
    }
    onInput() {
      this.setValue(this.input.value, false);
    }
  }
  customElements.define('quantity-control', QuantityControl);
}

function upgradeQuantityControl(root = document) {
  root.querySelectorAll('.product-quantity-controller:not([data-quantity-control])').forEach(el => {
    const comp = document.createElement('quantity-control');
    Array.from(el.attributes).forEach(attr => comp.setAttribute(attr.name, attr.value));
    comp.className = el.className;
    comp.dataset.quantityControl = 'true';
    while (el.firstChild) comp.appendChild(el.firstChild);
    if (el.parentNode) el.parentNode.replaceChild(comp, el);
  });
}

document.addEventListener('DOMContentLoaded', () => upgradeQuantityControl());
new MutationObserver(muts => {
  for (const m of muts) {
    if (m.addedNodes.length) {
      upgradeQuantityControl();
      break;
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

if (!customElements.get('quantity-control')) {
  subscribe(EVENT_TYPES.quantityUpdate, ({ el, value }) => {

    const container = el.closest('.shop-the-look-product-item') || document;
    const formQty = container.querySelector('product-form input.quick-add-quantity[name="quantity"]');
    if (formQty) formQty.value = value;
  });

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.classList.contains('js-product-form')) return;
    const card = form.closest('.shop-the-look-product-item');
    const input = card ? card.querySelector('.product-quantity') : null;
    const qtyValue = input ? parseInt(input.value, 10) : 1;
    let hiddenQty = form.querySelector('input.quick-add-quantity[name="quantity"]');
    if (!hiddenQty) {
      hiddenQty = document.createElement('input');
      hiddenQty.type = 'hidden';
      hiddenQty.name = 'quantity';
      hiddenQty.className = 'quick-add-quantity';
      form.appendChild(hiddenQty);
    }
    hiddenQty.value = isNaN(qtyValue) ? '1' : String(qtyValue);
  });

}

/* -----------------------------
   Announcement Drawer
   ----------------------------- */
if (!customElements.get('announcement-drawer')) {
  customElements.define('announcement-drawer', class AnnouncementDrawer extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.drawer = this.querySelector(".announcement-bar-drawer");
      this.toggler = this.querySelector(".announcement-bar-toggler");
      this.overlay = this.querySelector(".overlay");

      if (!this.drawer || !this.toggler || !this.overlay) return;

      this.toggler.addEventListener("click", () => {
        if (this.drawer.classList.contains("active")) {
          this.closeDrawer();
        } else {
          this.openDrawer();
        }
      });

      this.overlay.addEventListener("click", () => {
        this.closeDrawer();
      });
    }

    openDrawer() {
      this.drawer.setAttribute('aria-hidden', false);
      this.drawer.classList.add("active");
      this.toggler.classList.add("show");
      this.overlay.classList.add("show");
      document.body.style.overflow = "hidden";
    }

    closeDrawer() {
      this.drawer.setAttribute('aria-hidden', true);
      this.drawer.classList.remove("active");
      this.toggler.classList.remove("show");
      this.overlay.classList.remove("show");
      if (this.header) this.header.style.zIndex = "";
      document.body.style.overflow = "";
    }
  }
  );
}

/* -----------------------------
   Lazy loading video wrapper (fth-load-video)
   ----------------------------- */

if (!customElements.get('fth-load-video')) {
  customElements.define(
    'fth-load-video',
    class FTHLazyLoadingVideo extends HTMLElement {
      constructor() {
        super();

        this.iframe = this.querySelector("iframe");
        this.template = this.querySelector("template");
        this.isMouseenter = false;
      }

      loadVideo() {
        var t;

        if (this.iframe) {
          const src = this.iframe.getAttribute("data-src");
          if (!src) return;

          this.iframe.setAttribute("src", src);

          this.iframe.addEventListener(
            "load",
            function () {
              if (this.dataVideoType === "youtube") {
                this.iframe.contentWindow.postMessage(
                  '{"event":"command","func":"playVideo","args":""}',
                  "*"
                );
              }

              if (this.dataVideoType === "vimeo") {
                this.iframe.contentWindow.postMessage(
                  '{"method":"play"}',
                  "*"
                );
              }
            }.bind(this),
            { once: true }
          );
        }

        if (this.dataVideoType === "local_video") {
          this.local_video = this.querySelector("video");
          if (!this.local_video) return;

          const source = this.local_video.querySelector("source");
          t = source?.getAttribute("data-src");

          if (t) {
            source.setAttribute("src", t);
            this.local_video.load();
            this.local_video.play().catch(() => { });
          }
        }
      }

      execute() {
        this.loadVideo();
      }

      static get observedAttributes() {
        return ["data-video-type", "data-video-id"];
      }

      get dataVideoType() {
        return this.getAttribute("data-video-type");
      }

      get dataVideoId() {
        return this.getAttribute("data-video-id");
      }

      attributeChangedCallback(t, e, s) {
        if (e !== s) {
          this.loadVideo();
        }
      }

      connectedCallback() {
        this.execute();
      }

      disconnectedCallback() { }
    }
  );
}

/* -----------------------------
   FTHLoadMedia (main media lazy loader + YouTube/Vimeo/native control)
   ----------------------------- */

if (!customElements.get('fth-load-media')) {
  customElements.define('fth-load-media', class FTHLoadMedia extends HTMLElement {
    constructor() {
      super(),
        (this.$ = this.querySelector.bind(this)),
        (this.sectionID = this.dataset.sectionId),
        (this.idVideo = this.dataset.idVideo),
        (this.typeVideo = this.dataset.type),
        (this.eleVideo = `fthVideo-${this.sectionID}-` + this.idVideo),
        (this.onPlayerStateYTChange = this.onPlayerStateYTChange.bind(this)),
        (this.onPlayerPlay = this.onPlayerPlay.bind(this)),
        (this.playPauseButton = this.$(".video-play-pause-button")),
        (this.trigger = this.$(".js-load-media-trigger")),
        this.trigger && this.trigger.addEventListener("click", this.handlePlayVideo.bind(this));

      if (this.playPauseButton) {
        this.playPauseButton.addEventListener("click", this.togglePlayPauseVideo.bind(this));
      }


    }


    togglePlayPauseVideo() {
      this.classList.add("playing");
      this.loadContent();

      const video = this.$(".js-media-item-video");

      if (!video) return;

      // First time play: pause others, play this
      if (video.paused || video.ended) {
        this.pauseAllVideo(video);
        video.play();
        this.updatePlayPauseIcon(true);
      } else {
        video.pause();
        this.updatePlayPauseIcon(false);
      }


      if (this.player && typeof this.player.getPlayerState === "function") {
        const state = this.player.getPlayerState();
        if (state === YT.PlayerState.PAUSED || state === YT.PlayerState.ENDED) {
          this.pauseAllVideo(this.player);
          this.player.playVideo();
          this.updatePlayPauseIcon(true);
        } else if (state === YT.PlayerState.PLAYING) {
          this.player.pauseVideo();
          this.updatePlayPauseIcon(false);
        }
      }



    }

    updatePlayPauseIcon(isPlaying) {
      const playIcon = this.playPauseButton?.querySelector(".video-play-icon");
      const pauseIcon = this.playPauseButton?.querySelector(".video-pause-icon");

      if (!playIcon || !pauseIcon) return;

      if (isPlaying) {
        playIcon.style.display = "none";
        pauseIcon.style.display = "inline";
      } else {
        playIcon.style.display = "inline";
        pauseIcon.style.display = "none";
      }
    }



    handlePlayVideo() {
      this.classList.add("playing");
      this.loadContent();
      const t = this.querySelector(".js-media-item-video");
      t &&
        t.addEventListener("play", () => {
          this.pauseAllVideo(t), t.play();
        });
    }
    onYouTubeIframeAPIReady() {
      if ("undefined" != typeof YT && void 0 !== YT.Player)
        try {
          this.player = new YT.Player(this.eleVideo, {
            videoId: this.idVideo,
            playerVars: { playsinline: 1 },
            events: {
              onReady: this.onPlayerYTReady,
              onStateChange: this.onPlayerStateYTChange,
            },
          });
        } catch (t) {
          console.error("Lỗi khi tạo trình phát YouTube:", t);
        }
      else {
        var t = document.createElement("script"),
          e =
            ((t.src = "https://www.youtube.com/iframe_api"),
              (t.onload = () => {
                this.onYouTubeIframeAPIReady();
              }),
              document.getElementsByTagName("script")[0]);
        e.parentNode.insertBefore(t, e);
      }
    }
    onVimeoIframeAPIReady() {
      if ("undefined" != typeof Vimeo && void 0 !== Vimeo.Player)
        try {
          var t = { id: this.idVideo, autoplay: 1 };
          (this.playerVimeo = new Vimeo.Player(this.eleVideo, t)),
            this.playerVimeo.on("play", () => {
              this.pauseAllVideo(this.playerVimeo);
            });
        } catch (t) {
          console.error("Lỗi khi tạo trình phát Vimeo:", t);
        }
      else {
        var t = document.createElement("script"),
          e =
            ((t.src = "https://player.vimeo.com/api/player.js"),
              (t.onload = () => {
                this.onVimeoIframeAPIReady();
              }),
              document.getElementsByTagName("script")[0]);
        e.parentNode.insertBefore(t, e);
      }
    }
    onPlayerYTReady = () => {
      this.pauseAllVideo(this.player), this.player.playVideo();
    };
    // onPlayerStateYTChange = (t) => {
    //   1 == t.data && this.onPlayerPlay();
    // };

    onPlayerStateYTChange = (t) => {
      const state = t.data;

      if (state === YT.PlayerState.PLAYING) {
        this.onPlayerPlay(); // pause other videos
        this.updatePlayPauseIcon(true);
      } else if (
        state === YT.PlayerState.PAUSED ||
        state === YT.PlayerState.ENDED
      ) {
        this.updatePlayPauseIcon(false);
      }
    };



    onPlayerPlay = () => {
      this.pauseAllVideo(this.player);
    };
    loadContent() {
      if (!this.getAttribute("loaded")) {
        if (this.$("template")) {
          var t = this.$("template").content.firstElementChild.cloneNode(!0);
          this.appendChild(t),
            this.isLoaded(!0),
            this.trigger && this.trigger.remove();

          // 🔽 ADD HERE AFTER APPENDING VIDEO
          const video = this.$(".js-media-item-video");
          if (video && !video.hasAttribute("data-listeners-added")) {
            video.addEventListener("play", () => this.updatePlayPauseIcon(true));
            video.addEventListener("pause", () => this.updatePlayPauseIcon(false));
            video.addEventListener("ended", () => this.updatePlayPauseIcon(false));
            video.setAttribute("data-listeners-added", "true");
          }

        } else {
          if ("youtube" == this.typeVideo) this.onYouTubeIframeAPIReady();
          else {
            if ("vimeo" != this.typeVideo) return !0;
            this.onVimeoIframeAPIReady();
          }
          this.trigger?.classList.add("d-none"),
            this.$(".js-media-item").classList.add("d-flex");
          const video = this.$(".js-media-item-video");
          if (video && !video.hasAttribute("data-listeners-added")) {
            video.addEventListener("play", () => this.updatePlayPauseIcon(true));
            video.addEventListener("pause", () => this.updatePlayPauseIcon(false));
            video.addEventListener("ended", () => this.updatePlayPauseIcon(false));
            video.setAttribute("data-listeners-added", "true");
          }

        }
        return !0;
      }
    }
    pauseAllVideo(e) {
      document.querySelectorAll(
        ".js-product-media-deferred-video:has([data-type='youtube']"
      ).forEach((t) => {
        t.player && e !== t.player && t.player?.pauseVideo();
      }),
        document.querySelectorAll(
          ".js-product-media-deferred-video:has([data-type='vimeo'])"
        ).forEach((t) => {
          t.playerVimeo !== e && t.playerVimeo?.pause();
        }),
        document.querySelectorAll(".js-media-item-video").forEach((t) => {
          t !== e && t.pause();
        });
    }
    isLoaded(t) {
      t ? this.setAttribute("loaded", !0) : this.removeAttribute("loaded");
    }
  }
  );
}




if (!customElements.get('deferred-media')) {
  customElements.define(
    'deferred-media',
    class DeferredMedia extends HTMLElement {
      constructor() {
        super();

        this.$ = this.querySelector.bind(this);

        this.sectionID = this.dataset.sectionId;
        this.idVideo = this.dataset.idVideo;
        this.typeVideo = this.dataset.type;
        this.eleVideo = `DeferredVideo-${this.sectionID}-` + this.idVideo;

        this.trigger = this.$('.js-load-media-trigger');
        this.playPauseButton = this.$('.video-play-pause-button');


        this.onPlayerStateYTChange = this.onPlayerStateYTChange.bind(this);
        this.onPlayerPlay = this.onPlayerPlay.bind(this);

        this.trigger?.addEventListener('click', () => this.handleToggle());


      }

      connectedCallback() {
        if (Shopify && Shopify.designMode && this.getAttribute('data-autoplay') === 'true') {
          this.loadContent();
        } else if (this.getAttribute('data-autoplay') === 'true') {
          this.loadContent();
        }
      }

      /* ===============================
         TOGGLE
      =============================== */
      handleToggle() {
        this.classList.add('playing');
        this.loadContent();

        const video = this.$('.js-media-item-video') || this.$('.js-media-item-video-mobile');

        if (video) {
          if (video.paused || video.ended) {
            this.pauseAllVideo(video);
            video.play();
            this.setButtonState(true);
          } else {
            video.pause();
            this.setButtonState(false);
          }
        }

        if (this.player && typeof this.player.getPlayerState === 'function') {
          const state = this.player.getPlayerState();

          if (
            state === YT.PlayerState.PAUSED ||
            state === YT.PlayerState.ENDED
          ) {
            this.pauseAllVideo(this.player);
            this.player.playVideo();
            this.setButtonState(true);
          } else if (state === YT.PlayerState.PLAYING) {
            this.player.pauseVideo();
            this.setButtonState(false);
          }
        }
      }

      /* ===============================
         BUTTON STATE
      =============================== */
      setButtonState(isPlaying) {
        if (!this.playPauseButton) return;
        this.playPauseButton.dataset.playing = isPlaying ? 'true' : 'false';
      }

      /* ===============================
         YOUTUBE
      =============================== */
      onYouTubeIframeAPIReady() {
        if (typeof YT !== 'undefined' && YT.Player) {
          this.player = new YT.Player(this.eleVideo, {
            videoId: this.idVideo,
            playerVars: { playsinline: 1 },
            events: {
              onReady: this.onPlayerYTReady,
              onStateChange: this.onPlayerStateYTChange,
            },
          });
        } else {
          const script = document.createElement('script');
          script.src = 'https://www.youtube.com/iframe_api';
          script.onload = () => this.onYouTubeIframeAPIReady();
          document.head.appendChild(script);
        }
      }

      onPlayerYTReady = () => {
        this.pauseAllVideo(this.player);
        this.player.playVideo();
      };

      onPlayerStateYTChange(t) {
        if (t.data === YT.PlayerState.PLAYING) {
          this.onPlayerPlay();
          this.setButtonState(true);
        } else if (
          t.data === YT.PlayerState.PAUSED ||
          t.data === YT.PlayerState.ENDED
        ) {
          this.setButtonState(false);
        }
      }

      onPlayerPlay() {
        this.pauseAllVideo(this.player);
      }

      /* ===============================
         VIMEO
      =============================== */
      onVimeoIframeAPIReady() {
        if (typeof Vimeo !== 'undefined' && Vimeo.Player) {
          this.playerVimeo = new Vimeo.Player(this.eleVideo, {
            id: this.idVideo,
            autoplay: 1,
          });

          this.playerVimeo.on('play', () =>
            this.pauseAllVideo(this.playerVimeo)
          );
        } else {
          const script = document.createElement('script');
          script.src = 'https://player.vimeo.com/api/player.js';
          script.onload = () => this.onVimeoIframeAPIReady();
          document.head.appendChild(script);
        }
      }

      /* ===============================
         LOAD CONTENT
      =============================== */

      loadContent() {
        if (this.hasAttribute('loaded')) return;

        // 🔹 YOUTUBE / VIMEO → hide trigger only
        if (this.typeVideo === 'youtube' || this.typeVideo === 'vimeo') {
          this.trigger?.classList.add('d-none');
        }

        // 🔹 Load template if exists
        if (this.$('template')) {
          const node = this.$('template').content.firstElementChild.cloneNode(true);
          this.appendChild(node);

        } else {
          if (this.typeVideo === 'youtube') {
            this.onYouTubeIframeAPIReady();
            this.buttonWrapper?.classList.add('display-none');
          }

          if (this.typeVideo === 'vimeo') {
            this.onVimeoIframeAPIReady();
          }
        }


        // 🔹 Handle autoplay (lazy-load logic)
        if (this.getAttribute('data-autoplay') === 'true') {


          // HTML5 video
          const localVideo = this.$('video');


          if (localVideo) {
            const source = localVideo.querySelector('source');
            const src = source?.getAttribute('data-src');
            if (src) {
              source.setAttribute('src', src);
              localVideo.load();
              localVideo.muted = true
              localVideo.play().catch((err) => {
                console.warn("Autoplay failed:", err);
              });
            }
            else {
              console.warn("Missing data-src on local video source.");
            }
          }

          // iframe video (YouTube/Vimeo)
          const iframe = this.$('iframe');
          if (iframe) {
            const src = iframe.getAttribute('data-src');
            if (src) {
              iframe.setAttribute('src', src);

              iframe.addEventListener(
                'load',
                () => {
                  if (this.typeVideo === 'youtube') {
                    iframe.contentWindow.postMessage(
                      '{"event":"command","func":"playVideo","args":""}',
                      '*'
                    );
                  }

                  if (this.typeVideo === 'vimeo') {
                    iframe.contentWindow.postMessage('{"method":"play"}', '*');
                  }
                },
                { once: true }
              );
            }
          }
        }

        this.isLoaded(true);

        // 🔹 Trigger listeners remain
        const video = this.$('.js-media-item-video');
        if (video && !video.dataset.listenersAdded) {
          video.addEventListener('play', () => this.setButtonState(true));
          video.addEventListener('pause', () => this.setButtonState(false));
          video.addEventListener('ended', () => this.setButtonState(false));
          video.dataset.listenersAdded = 'true';
        }
      }

      /* ===============================
         PAUSE ALL
      =============================== */
      pauseAllVideo(current) {
        document
          .querySelectorAll(
            ".js-product-media-deferred-video:has([data-type='youtube'])"
          )
          .forEach((el) => {
            el.player && el.player !== current && el.player.pauseVideo();
          });

        document
          .querySelectorAll(
            ".js-product-media-deferred-video:has([data-type='vimeo'])"
          )
          .forEach((el) => {
            el.playerVimeo &&
              el.playerVimeo !== current &&
              el.playerVimeo.pause();
          });

        document
          .querySelectorAll('.js-media-item-video')
          .forEach((v) => v !== current && v.pause());
      }

      isLoaded(val) {
        val ? this.setAttribute('loaded', '') : this.removeAttribute('loaded');
      }
    }
  );
}







if (!customElements.get('deferred-media-product')) {
  customElements.define(
    'deferred-media-product',
    class DeferredMedia extends HTMLElement {
      constructor() {
        super();

        this.$ = this.querySelector.bind(this);

        this.typeVideo = this.dataset.type; // youtube | vimeo | video
        this.trigger = this.$('.js-load-media-trigger');
        this.playPauseButton = this.$('.video-play-pause-button');

        this.mediaEl = null;
        this.player = null;
        this.playerVimeo = null;

        this.onPlayerStateYTChange = this.onPlayerStateYTChange.bind(this);

        this.trigger?.addEventListener('click', () => this.handleToggle());
      }

      connectedCallback() {
        if (this.getAttribute('data-autoplay') === 'true') {
          this.loadContent();
        }
      }

      /* ===============================
         LOAD CONTENT (AUTOPLAY SAFE)
      =============================== */
      loadContent() {
        if (this.hasAttribute('loaded')) return;

        // 1️. Clone template
        const template = this.$('template');
        if (template) {
          const node = template.content.firstElementChild.cloneNode(true);
          this.appendChild(node);
        }

        // 2️. Detect media
        this.mediaEl = this.$('iframe') || this.$('video');
        if (!this.mediaEl) return;

        // 3️. LAZY LOAD (data-src → src)
        if (this.mediaEl.tagName === 'IFRAME') {
          const src = this.mediaEl.getAttribute('data-src');
          if (src) this.mediaEl.setAttribute('src', src);
        }

        if (this.mediaEl.tagName === 'VIDEO') {
          const source = this.mediaEl.querySelector('source');
          const src = source?.getAttribute('data-src');
          if (src) {
            source.setAttribute('src', src);
            this.mediaEl.load();
          }
        }

        // 4️. Hide trigger for iframe videos

        if (this.typeVideo === 'youtube' || this.typeVideo === 'vimeo') {
          this.trigger?.classList.add('d-none');
        }

        // 5. Init player by type
        if (this.typeVideo === 'youtube') this.initYouTube();
        if (this.typeVideo === 'vimeo') this.initVimeo();
        if (this.typeVideo === 'video') this.initHTML5Video();

        this.setAttribute('loaded', '');
      }

      /* ===============================
         TOGGLE
      =============================== */
      handleToggle() {
        this.classList.add('playing');
        this.loadContent();

        // HTML5 VIDEO
        if (this.mediaEl?.tagName === 'VIDEO') {
          if (this.mediaEl.paused) {
            this.pauseAllVideo(this.mediaEl);
            this.mediaEl.play();
            this.setButtonState(true);
            this.togglePlayingClass(true);
          } else {
            this.mediaEl.pause();
            this.setButtonState(false);
            this.togglePlayingClass(false);
          }
        }

        // YOUTUBE
        if (this.player) {
          const state = this.player.getPlayerState();
          if (
            state === YT.PlayerState.PAUSED ||
            state === YT.PlayerState.ENDED
          ) {
            this.pauseAllVideo(this.player);
            this.player.playVideo();
            this.setButtonState(true);
            this.togglePlayingClass(true);
          } else {
            this.player.pauseVideo();
            this.setButtonState(false);
            this.togglePlayingClass(false);
          }
        }

        // VIMEO
        if (this.playerVimeo) {
          this.playerVimeo.getPaused().then((paused) => {
            if (paused) {
              this.pauseAllVideo(this.playerVimeo);
              this.playerVimeo.play();
              this.setButtonState(true);
              this.togglePlayingClass(true);
            } else {
              this.playerVimeo.pause();
              this.setButtonState(false);
              this.togglePlayingClass(false);
            }
          });
        }
      }

      togglePlayingClass(isPlaying) {
        this.classList.toggle('is-playing', isPlaying);
      }


      /* ===============================
         BUTTON STATE
      =============================== */

      setButtonState(isPlaying) {
        if (!this.playPauseButton) return;
        this.playPauseButton.dataset.playing = isPlaying ? 'true' : 'false';
      }

      /* ===============================
         YOUTUBE
      =============================== */
      initYouTube() {
        if (typeof YT !== 'undefined' && YT.Player) {
          this.player = new YT.Player(this.mediaEl, {
            events: {
              onReady: () => {
                this.pauseAllVideo(this.player);
                if (this.getAttribute('data-autoplay') === 'true') {
                  this.player.playVideo();
                  this.togglePlayingClass(true);
                }
              },
              onStateChange: this.onPlayerStateYTChange,
            },
          });
        } else {
          const script = document.createElement('script');
          script.src = 'https://www.youtube.com/iframe_api';
          script.onload = () => this.initYouTube();
          document.head.appendChild(script);
        }
      }

      onPlayerStateYTChange(e) {
        if (e.data === YT.PlayerState.PLAYING) {
          this.pauseAllVideo(this.player);
          this.setButtonState(true);
          this.togglePlayingClass(true);
        }

        if (
          e.data === YT.PlayerState.PAUSED ||
          e.data === YT.PlayerState.ENDED
        ) {
          this.setButtonState(false);
          this.togglePlayingClass(false);
        }
      }

      /* ===============================
         VIMEO
      =============================== */
      initVimeo() {
        if (typeof Vimeo !== 'undefined' && Vimeo.Player) {
          this.playerVimeo = new Vimeo.Player(this.mediaEl);

          this.playerVimeo.on('play', () => {
            this.pauseAllVideo(this.playerVimeo);
            this.setButtonState(true);
            this.togglePlayingClass(true);
          });

          this.playerVimeo.on('pause', () => {
            this.setButtonState(false);
            this.togglePlayingClass(false);
          }
          );
          this.playerVimeo.on('ended', () => {
            this.setButtonState(false);
            this.togglePlayingClass(false);
          });

        } else {
          const script = document.createElement('script');
          script.src = 'https://player.vimeo.com/api/player.js';
          script.onload = () => this.initVimeo();
          document.head.appendChild(script);
        }
      }

      /* ===============================
         HTML5 VIDEO
      =============================== */
      initHTML5Video() {
        this.mediaEl.addEventListener('play', () => {
          this.pauseAllVideo(this.mediaEl);
          this.setButtonState(true);
          this.togglePlayingClass(true);
        });

        this.mediaEl.addEventListener('pause', () => {
          this.setButtonState(false);
          this.togglePlayingClass(false);
        }
        );

        this.mediaEl.addEventListener('ended', () => {
          this.setButtonState(false)
          this.togglePlayingClass(false);
        }
        );

        if (this.getAttribute('data-autoplay') === 'true') {
          this.mediaEl.muted = true;
          this.mediaEl.play().catch(() => { });
        }
      }

      /* ===============================
         PAUSE ALL
      =============================== */
      pauseAllVideo(current) {
        document.querySelectorAll('deferred-media').forEach((el) => {
          if (el !== this) {
            el.player && el.player !== current && el.player.pauseVideo();
            el.playerVimeo &&
              el.playerVimeo !== current &&
              el.playerVimeo.pause();
            const v = el.querySelector('video');
            v && v !== current && v.pause();
          }
        });
      }
    }
  );
}

 
/* -----------------------------
   slideshow Swiper (uses Swiper)
   ----------------------------- */
if (!customElements.get('slideshow-swiper')) {
  customElements.define('slideshow-swiper', class slideshowSwiper extends HTMLElement {
    constructor() {
      super();
      this.slider;
      this.resizeObserver;
      window.addEventListener("resize", this.handleResize.bind(this));
    }

    connectedCallback() {
      this.sliderWrapper = this.querySelector(".slideshow__swiper");
      this.optionsData = this.sliderWrapper.dataset.option ? JSON.parse(this.sliderWrapper.dataset.option) : {};
      this.desktopslider = this.optionsData.desktopSlider;
      this.grid = this.optionsData.grid;
      this.resizeObserver = new ResizeObserver((entries) => this.initials());
      this.resizeObserver.observe(this.sliderWrapper);
    }

    initials() {
      if (this.desktopslider == false) {
        if (this.slider) {
          this.slider.destroy(true, true);
          this.slider = null;
        }
        if (window.innerWidth < 768) {
          this.initSlider();
        }
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      let next = this.querySelector(".arrow-next-button");
      let prev = this.querySelector(".arrow-prev-button");
      let pagination = this.querySelector(".swiper-pagination");
      let scrollbar = this.querySelector(".swiper-scrollbar");

      let defaults = {
        effect: "slide",
        slidesPerView: "auto",
        centeredSlides: false,
        grabCursor: false,
        horizontal: true,
        watchSlidesProgress: true,
        draggable: !0,
        autoHeight: !1,
        watchOverflow: !0,
        threshold: 10,
        speed: 700,
        freeMode: false,
        loop: false,
        autoplay: {
          enabled: false,
          disableOnInteraction: false,
          delay: 1500,
        },
        mousewheel: {
          forceToAxis: true,
        },
        navigation: {
          nextEl: next,
          prevEl: prev,
        },
        scrollbar: {
          el: scrollbar,
          draggable: true,
        },
        pagination: {
          el: pagination,
          clickable: true,
        },
      };

      let options = { ...defaults, ...this.optionsData };

      this.slider = new Swiper(this.sliderWrapper, options);
      this.slider.on("slideChangeTransitionStart", () => {
        this.handleSlideChange();
      });
    }


    handleSlideChange() {
      if (this.querySelectorAll("fth-load-media").length > 0) {


        this.querySelectorAll("fth-load-media").forEach((mediaEl) => {
          // YouTube player API
          if (mediaEl.player && typeof mediaEl.player.pauseVideo === "function") {
            mediaEl.player.pauseVideo();
          }

          // Vimeo player API
          if (
            mediaEl.playerVimeo &&
            typeof mediaEl.playerVimeo.pause === "function"
          ) {
            mediaEl.playerVimeo.pause();
          }

          // Native video
          mediaEl.querySelectorAll(".js-media-item-video").forEach((vid) => {
            if (!vid.paused) {
              vid.pause();
            }
          });

          // Fallback: Reset any raw iframe that is NOT API controlled
          mediaEl.querySelectorAll("iframe").forEach((iframe) => {
            const isYouTube = iframe.src.includes("youtube.com");
            const isVimeo = iframe.src.includes("vimeo.com");
            // Only if no API player exists on this mediaEl
            if (!mediaEl.player && !mediaEl.playerVimeo && (isYouTube || isVimeo)) {
              const currentSrc = iframe.src;
              iframe.src = currentSrc; // Reset iframe (force reload)
            }
          });
        });
      }


      if (this.querySelectorAll("deferred-media-product").length > 0) {


        this.querySelectorAll("deferred-media-product").forEach((mediaEl) => {
          // YouTube player API
          if (mediaEl.player && typeof mediaEl.player.pauseVideo === "function") {
            mediaEl.player.pauseVideo();
          }

          // Vimeo player API
          if (
            mediaEl.playerVimeo &&
            typeof mediaEl.playerVimeo.pause === "function"
          ) {
            mediaEl.playerVimeo.pause();
          }

          // Native video
          mediaEl.querySelectorAll(".js-media-item-video").forEach((vid) => {
            if (!vid.paused) {
              vid.pause();
            }
          });

          mediaEl.querySelectorAll('.video-play-pause-button').forEach(trigger => {
            if (trigger != null) {
              trigger.dataset.playing = 'false';
            }
          });

          // Fallback: Reset any raw iframe that is NOT API controlled
          mediaEl.querySelectorAll("iframe").forEach((iframe) => {
            const isYouTube = iframe.src.includes("youtube.com");
            const isVimeo = iframe.src.includes("vimeo.com");
            // Only if no API player exists on this mediaEl
            if (!mediaEl.player && !mediaEl.playerVimeo && (isYouTube || isVimeo)) {
              const currentSrc = iframe.src;
              iframe.src = currentSrc; // Reset iframe (force reload)
            }
          });
        });
      }


    }

    handleResize() {
      this.initials();
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.unobserve(this.sliderWrapper);
      }
      if (this.slider) {
        this.slider.destroy(true, true);
      }
    }
  }
  );
}







/* -----------------------------
   hoverImages
   ----------------------------- */
if (!customElements.get("hover-images")) {
  customElements.define('hover-images', class hoverImages extends HTMLElement {
    connectedCallback() {
      const swiperEl = this.querySelector('.product-card__media-slider');
      if (swiperEl) {
        new Swiper(swiperEl, {
          loop: false,
          slidesPerView: 1,
          spaceBetween: 10,
          allowTouchMove: false,
          navigation: {
            nextEl: this.querySelector('.arrow-prev-button-js'),
            prevEl: this.querySelector('.arrow-next-button-js'),
          },
          breakpoints: {
            359: {
              allowTouchMove: false,
            },
            768: {
              allowTouchMove: true
            }
          },
        });
      }
    }
  }
  );
}


/* -----------------------------
   collapsible content
   ----------------------------- */



if (!customElements.get("accordion-content")) {
  class accordionContent extends HTMLElement {
    constructor() {
      super();
      this.details = this.querySelector('details');
      this.toggle = this.querySelector('summary');
      this.panel = this.toggle.nextElementSibling;
      this.init();


    }

    init() {



      // Check if the content element has a CSS transition.
      if (window.getComputedStyle(this.panel).transitionDuration !== '0s') {
        this.toggle.addEventListener('click', this.handleToggle.bind(this));
        this.details.addEventListener('transitionend', this.handleTransitionEnd.bind(this));

      }
    }

    /**
     * Handles 'click' events on the summary element.
     * @param {object} evt - Event object.
     */
    handleToggle(evt) {
      evt.preventDefault();

      console.log("Tress", this.panel);



      if (!this.details.open) {
        this.open();
      } else {
        this.close();
      }
    }

    /**
     * Handles 'transitionend' events on the details element.
     * @param {object} evt - Event object.
     */
    handleTransitionEnd(evt) {
      if (evt.target !== this.panel) return;

      if (this.details.classList.contains('is-closing')) {
        this.details.classList.remove('is-closing');
        this.details.open = false;
      }

      this.panel.removeAttribute('style');
    }

    /**
     * Adds inline 'height' style to the content element, to trigger open transition.
     */
    addContentHeight() {
      this.panel.style.height = `${this.panel.scrollHeight}px`;
    }

    /**
     * Opens the details element.
     */
    open() {


      // Set content 'height' to zero before opening the details element.
      this.panel.style.height = '0';

      // Open the details element
      this.details.open = true;

      // Set content 'height' to its scroll height, to enable CSS transition.
      this.addContentHeight();
    }

    /**
     * Closes the details element.
     */
    close() {
      // Set content height to its scroll height, to enable transition to zero.
      this.addContentHeight();

      // Add class to enable styling of content or toggle icon before or during close transition.
      this.details.classList.add('is-closing');

      // Set content height to zero to trigger the transition.
      // Slight delay required to allow scroll height to be applied before changing to '0'.
      setTimeout(() => {
        this.panel.style.height = '0';
      });
    }
  }
  customElements.define('accordion-content', accordionContent);
}




if (!customElements.get('collapsible-content')) {
  customElements.define(
    'collapsible-content',
    class collapsibleContent extends HTMLElement {
      constructor() {
        super();
        this.item = this.querySelector('.collapsiable-item');
        this.toggle = this.querySelector('.collapsiable-item-title');
        this.details = this.querySelector('.collapsiable-item-deails');
        this.panel = this.querySelector('.disclosure__panel');
        this.init();
      }

      init() {
        if (!this.toggle || !this.details || !this.panel || !this.item) return;

        // Check if is-open already exists (default open)
        if (this.item.classList.contains('is-open')) {
          this.details.open = true;
          this.panel.style.height = this.panel.scrollHeight + 'px';
        }

        // Click to toggle
        this.toggle.addEventListener('click', () => {
          this.details.open ? this.close() : this.open();
        });
      }

      open() {
        this.item.classList.add('is-open');
        this.panel.style.height = '0px';
        this.details.open = true;

        requestAnimationFrame(() => {
          this.panel.style.height = this.panel.scrollHeight + 'px';
        });
      }

      close() {
        this.item.classList.remove('is-open');
        this.panel.style.height = this.panel.scrollHeight + 'px';

        requestAnimationFrame(() => {
          this.panel.style.height = '0px';
        });

        this.panel.addEventListener(
          'transitionend',
          () => {
            this.details.open = false;
            this.panel.removeAttribute('style');
          },
          { once: true }
        );
      }
    }
  );
}


if (!customElements.get("collapsible-mobilecontent")) {
  class CollapsibleMobileContent extends HTMLElement {
    constructor() {
      super();
      this.detailsList = Array.from(this.querySelectorAll('details'));
      this.isMobile = window.innerWidth < 768;

      this.boundHandleResize = this.handleResize.bind(this);
      this.boundToggleHandlers = new Map();

      this.init();
      window.addEventListener('resize', this.boundHandleResize);
    }

    init() {
      if (this.isMobile) {
        this.enableAccordion();
      } else {
        this.disableAccordion();
      }
    }

    handleResize() {
      const nowMobile = window.innerWidth < 768;
      if (nowMobile !== this.isMobile) {
        this.isMobile = nowMobile;
        if (this.isMobile) {
          this.enableAccordion();
        } else {
          this.disableAccordion();
        }
      }
    }

    enableAccordion() {
      this.detailsList.forEach(details => {
        const toggle = details.querySelector('summary');
        const panel = toggle.nextElementSibling;

        // Reset for mobile mode
        panel.style.height = '0';
        details.open = false;
        details.classList.remove('is-closing');

        // Create click handler for this details
        const handler = (evt) => {
          evt.preventDefault();
          if (!details.open) {
            // Opening animation
            panel.style.height = '0';
            details.open = true;
            requestAnimationFrame(() => {
              panel.style.height = `${panel.scrollHeight}px`;
            });
          } else {
            // Closing animation
            panel.style.height = `${panel.scrollHeight}px`;
            details.classList.add('is-closing');
            setTimeout(() => {
              panel.style.height = '0';
            }, 10);
          }
        };

        if (!this.boundToggleHandlers.has(details)) {
          toggle.addEventListener('click', handler);
          this.boundToggleHandlers.set(details, handler);

          // Transition end cleanup
          panel.addEventListener('transitionend', (evt) => {
            if (evt.target !== panel) return;
            if (details.classList.contains('is-closing')) {
              details.classList.remove('is-closing');
              details.open = false;
              panel.style.height = '';
            }
          });
        }
      });
    }

    disableAccordion() {
      this.detailsList.forEach(details => {
        const toggle = details.querySelector('summary');
        const panel = toggle.nextElementSibling;

        // Remove click handlers if present
        const handler = this.boundToggleHandlers.get(details);
        if (handler) {
          toggle.removeEventListener('click', handler);
          this.boundToggleHandlers.delete(details);
        }

        // Reset styles for desktop view
        panel.style.height = '';
        details.classList.remove('is-closing');
        details.open = true;
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundHandleResize);
      this.detailsList.forEach(details => {
        const toggle = details.querySelector('summary');
        const handler = this.boundToggleHandlers.get(details);
        if (handler) {
          toggle.removeEventListener('click', handler);
        }
      });
      this.boundToggleHandlers.clear();
    }
  }

  customElements.define('collapsible-mobilecontent', CollapsibleMobileContent);
}



if (!customElements.get("accordion-filter")) {
  class AccordionCustom extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector("details");
      this.summary = this.querySelector("summary");
      this.panel = this.summary?.nextElementSibling;

      this.isDesktop = window.matchMedia("(min-width: 769px)");
      this.isMobile = window.matchMedia("(max-width: 768px)");

      this.handleToggle = this.handleToggle.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
      this.handleEscape = this.handleEscape.bind(this);
    }

    connectedCallback() {
      if (!this.details || !this.summary || !this.panel) return;

      this.initState(true);

      this.summary.addEventListener("click", this.handleToggle);
      this.panel.addEventListener("transitionend", this.handleTransitionEnd);

      document.addEventListener("click", this.handleOutsideClick);

      if (this.hasAttribute("data-close-with-escape")) {
        document.addEventListener("keydown", this.handleEscape);
      }
    }

    disconnectedCallback() {
      this.summary?.removeEventListener("click", this.handleToggle);
      this.panel?.removeEventListener("transitionend", this.handleTransitionEnd);
      document.removeEventListener("click", this.handleOutsideClick);
      document.removeEventListener("keydown", this.handleEscape);
    }

    // -----------------------------
    // MODES
    // -----------------------------
    isDesktopMode() {
      return this.isDesktop.matches;
    }

    isMobileMode() {
      return this.isMobile.matches;
    }

    isForceOpenDefault() {
      return (
        this.isDesktopMode() &&
        this.hasAttribute("open-by-default-on-desktop")
      );
    }

    isForceOpenDefaultMobile() {
      return (
        this.isMobileMode() &&
        this.hasAttribute("open-by-default-on-mobile")
      );
    }

    isDropdownMode() {
      return (
        this.isDesktopMode() &&
        this.dataset.disableAnimationOnDesktop === "true"
      );
    }

    // -----------------------------
    // INIT
    // -----------------------------
    initState(initial = false) {
      if (!initial) return;

      const shouldOpen =
        this.details.hasAttribute("open") || // 🔥 Shopify default respect
        this.isForceOpenDefault() ||
        this.isForceOpenDefaultMobile();

      this.details.open = shouldOpen;

      requestAnimationFrame(() => {
        if (this.details.open) {
          this.panel.style.height = "auto";
        } else {
          this.panel.style.height = "0px";
        }
      });
    }

    // -----------------------------
    // TOGGLE
    // -----------------------------
    handleToggle(e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.details.open) {
        this.close();
      } else {
        this.open();
      }
    }

    // -----------------------------
    // OPEN
    // -----------------------------
    open() {
      if (this.isDropdownMode()) {
        this.closeOthers();

        this.details.classList.remove("is-opening", "is-closing");
        this.details.open = true;

        this.details.offsetHeight;

        requestAnimationFrame(() => {
          this.details.classList.add("is-opening");
        });

        return;
      }

      this.panel.style.height = "0px";
      this.details.classList.remove("is-closing");

      this.details.open = true;

      requestAnimationFrame(() => {
        this.panel.style.height = this.panel.scrollHeight + "px";
      });
    }

    // -----------------------------
    // CLOSE
    // -----------------------------
    close() {
      if (this.isDropdownMode()) {
        this.details.classList.remove("is-opening");
        this.details.classList.add("is-closing");

        setTimeout(() => {
          this.details.open = false;
          this.details.classList.remove("is-closing");
        }, 350);

        return;
      }

      this.panel.style.height = this.panel.scrollHeight + "px";
      this.details.classList.add("is-closing");

      requestAnimationFrame(() => {
        this.panel.style.height = "0px";
      });
    }

    // -----------------------------
    // CLOSE OTHERS
    // -----------------------------
    closeOthers() {
      document.querySelectorAll("accordion-filter").forEach((el) => {
        if (el !== this) {
          const d = el.details;

          if (d?.open) {
            d.classList.remove("is-opening");
            d.classList.add("is-closing");

            setTimeout(() => {
              d.open = false;
              d.classList.remove("is-closing");
              d.offsetHeight;
            }, 350);
          }
        }
      });
    }

    // -----------------------------
    // TRANSITION END
    // -----------------------------
    handleTransitionEnd(e) {
      if (e.target !== this.panel) return;

      if (this.details.classList.contains("is-closing")) {
        this.details.classList.remove("is-closing");
        this.details.open = false;
      }

      if (!this.isDropdownMode()) {
        this.panel.style.height = this.details.open ? "auto" : "0px";
      }
    }

    // -----------------------------
    // OUTSIDE CLICK
    // -----------------------------
    handleOutsideClick(e) {
      if (!this.details.open) return;
      if (this.contains(e.target)) return;

      // 🔥 default-open হলে close করবে না
      if (this.isForceOpenDefault() || this.isForceOpenDefaultMobile()) return;

      this.close();
    }

    // -----------------------------
    // ESC
    // -----------------------------
    handleEscape(e) {
      if (e.key !== "Escape") return;

      if (this.isForceOpenDefault() || this.isForceOpenDefaultMobile()) return;

      this.close();
    }
  }

  customElements.define("accordion-filter", AccordionCustom);
}
/* -----------------------------
   newsletter form 
   ----------------------------- */
if (!customElements.get('newsletter-form')) {
  customElements.define('newsletter-form', class NewsletterForm extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    init() {
      this.form = this.querySelector('form');
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }
    }

    async handleSubmit(e) {
      e.preventDefault();
      const submitBtn = this.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.add('loading');
        submitBtn.setAttribute('aria-disabled', 'true');
      }

      try {
        const formData = new FormData(this.form);
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: formData
        });

        // Handle Shopify Challenge Redirect (CAPTCHA)
        if (response.redirected && response.url.includes('/challenge')) {
          window.location.href = response.url;
          return;
        }

        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');

        const newNewsletterForm = doc.querySelector('newsletter-form');
        if (newNewsletterForm) {
          this.innerHTML = newNewsletterForm.innerHTML;
          this.init();

          const message = this.querySelector('.form__message');
          if (message) message.focus();
        }

      } catch (err) {
        console.error('Newsletter submission error:', err);
      } finally {
        if (submitBtn) {
          submitBtn.classList.remove('loading');
          submitBtn.removeAttribute('aria-disabled');
        }
      }
    }
  });
}

/* -----------------------------
   product form 
   ----------------------------- */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class productForm extends HTMLElement {
      constructor() {
        super();
      }
      connectedCallback() {
        this.form = this.querySelector('.js-product-form') || this.querySelector('.js-product-form-main');
        if (this.form != null) {
          this.form.querySelector('[name=id]').disabled = false;
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
          this.cart = document.querySelector('side-cart');
          this.submitBtn = this.querySelector('[type="submit"]');
          this.submitBtnText = this.submitBtn.querySelector('span');

          if (document.querySelector('side-cart')) this.submitBtn.setAttribute('aria-haspopup', 'dialog');
          this.hideErrors = this.dataset.hideErrors === 'true';

        }

      }
      

      
      /**
       * Handles submission of the quick add.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();
        this.errorMsg = null;
        this.handleErrorMsg();

        if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;
        this.submitBtn.setAttribute('aria-disabled', true);
        this.querySelector('.loading-overlay').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        try {
          const response = await fetch(`${theme.routes.cart_add_url}`, config).then(r => r.json());

          if (response.status) {
            publish(EVENT_TYPES.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });

            this.handleErrorMsg(response.description);
            this.showToastMessage(response.description);
            this.submitBtn.setAttribute('aria-disabled', true);

            if (response.description.includes('maximum quantity')) {
              this.error = true;
              this.submitBtn.removeAttribute('aria-disabled');
              this.querySelector('.loading-overlay').classList.add('hidden');
              return;
            }
            

            const soldOut = this.submitBtn.querySelector('.sold-out');
            if (!soldOut) return;
            this.submitBtn.setAttribute('aria-disabled', true);
            this.submitBtn.querySelector('span').classList.add('hidden');
            soldOut.classList.remove('hidden');
            this.error = true;
            return;

          } else if (!this.cart) {
            window.location = theme.routes.cart_url;
            return;
          }

          if (!this.error) {
            publish(EVENT_TYPES.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }

          this.error = false;
          this.submitBtn.setAttribute('aria-disabled', true);
          this.querySelector('.loading-overlay').classList.add('hidden');
          this.submitBtn.classList.add('is-success');



          setTimeout(() => {
            this.querySelector('.loading-overlay').classList.add('hidden');
            this.submitBtn.classList.remove('is-success');
            this.submitBtn.removeAttribute('aria-disabled');

            const quickModal = this.closest('quick-add-modal');
          if (quickModal) {
            quickModal.hide();
          }

            this.cart.renderContents(response);
          }, 1000);


        } catch (e) {
          console.error(e);
        } finally {

          // this.submitBtn.classList.remove('is-loading');

          if (this.cart && this.cart.classList.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }

          if (!this.error) {
            this.submitBtn.removeAttribute('aria-disabled');
          }
        }
      }

      /**
       * Shows/hides an error message.
       * @param {string} [error=false] - Error to show a message for.
       */
      handleErrorMsg(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorWrapper = this.errorWrapper || this.querySelector('[data-error-wrapper]');
        if (!this.errorWrapper) return;
        this.errorMessage = this.errorMessage || this.errorWrapper.querySelector('[data-error-message]');
        this.errorWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }


      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitBtn.setAttribute('disabled', 'disabled');
          if (text) this.submitBtnText.textContent = text;
        } else {
          this.submitBtn.removeAttribute('disabled');
          this.submitBtn.textContent = theme.variantStrings.addToCart;
        }
      }

      showToastMessage(message = '') {
        if (!message) return;

        // Get wrapper and message element
        this.toastWrapper = document.querySelector('[data-toast-wrapper]');
        this.toastMessage = this.toastWrapper.querySelector('[data-toast-message]');

        if (!this.toastWrapper || !this.toastMessage) return;

        // Set message and show
        this.toastMessage.textContent = message;
        this.toastWrapper.classList.add('active');

        // Clear previous timeout if exists
        clearTimeout(this._toastTimeout);

        // Auto hide after 5 seconds
        this._toastTimeout = setTimeout(() => {
          this.toastWrapper.classList.remove('active');
          this.toastMessage.textContent = '';
        }, 5000);
      }

    }
  );
}


/* -----------------------------
   Tabs
   ----------------------------- */


if (!customElements.get('collection-tab-content')) {
  customElements.define(
    'collection-tab-content',
    class Tabs extends HTMLElement {
      constructor() {
        super();

        this.tabList = this.querySelector('[role="tablist"]');
        this.activeTab = this.tabList.querySelector('[aria-selected="true"]');
        this.isVerticalTablist =
          this.tabList.getAttribute('aria-orientation') === 'vertical';
        this.tabs = this.querySelectorAll('[role="tab"]');
        this.panels = this.querySelectorAll('[role="tabpanel"]');

        // Create active indicator
        this.tabActiveEl = document.createElement('div');
        this.tabActiveEl.classList.add('tabs-button-active-shape');
        this.tabList.appendChild(this.tabActiveEl);

        if (!this.activeTab) this.activeTab = this.tabs[0];

        this.activateTab(this.activeTab, true);
        this.addListeners();
      }

      addListeners() {
        this.tabList.addEventListener('click', this.handleClick.bind(this));
        this.tabList.addEventListener('keydown', this.handleKeydown.bind(this));
        window.addEventListener('resize', () => this.updateActiveIndicator());
      }

      handleClick(evt) {
        if (!evt.target.matches('[role="tab"]') || evt.target === this.activeTab) return;

        this.activateTab(evt.target);
      }

      handleKeydown(evt) {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (!keys.includes(evt.key)) return;

        evt.preventDefault();

        if (evt.key === 'Home') return this.activateTab(this.tabs[0]);
        if (evt.key === 'End') return this.activateTab(this.tabs[this.tabs.length - 1]);
        this.switchTabOnKeyPress(evt.key);
      }

      switchTabOnKeyPress(key) {
        let newTab;
        if (key === 'ArrowRight' || key === 'ArrowDown') {
          newTab = this.activeTab.nextElementSibling ?? this.tabs[0];
        } else {
          newTab = this.activeTab.previousElementSibling ?? this.tabs[this.tabs.length - 1];
        }
        this.activateTab(newTab);
      }

      activateTab(tab, skipAnimation = false) {
        this.deactivateActiveTab();

        Tabs.setTabState(tab, true);
        tab.removeAttribute('tabindex');
        tab.classList.add('is-active');
        this.activeTab = tab;
        const panelId = tab.getAttribute('aria-controls');
        const panel = document.getElementById(panelId);
        if (panel) {
          panel.classList.add('is-active');
        }
        this.updateActiveIndicator(skipAnimation);
        if (document.activeElement.matches('[role="tab"]')) {
          tab.focus();
        }
      }

      deactivateActiveTab() {
        if (!this.activeTab) return;
        Tabs.setTabState(this.activeTab, false);
        this.activeTab.setAttribute('tabindex', '-1');
        this.activeTab.classList.remove('is-active');


        const panelId = this.activeTab.getAttribute('aria-controls');
        const panel = document.getElementById(panelId);
        if (panel) panel.classList.remove('is-active');
        this.activeTab = null;
      }

      updateActiveIndicator(skipAnimation = false) {
        if (!this.activeTab) return;

        const left = this.activeTab.offsetLeft;
        const width = this.activeTab.offsetWidth;
        this.tabActiveEl.style.transition = skipAnimation ? 'none' : 'transform 0.3s ease, width 0.3s ease';
        this.tabActiveEl.style.transform = `translateX(${left}px)`;
        this.tabActiveEl.style.width = `${width}px`;
        if (skipAnimation) {
          void this.tabActiveEl.offsetWidth;
          this.tabActiveEl.style.transition = 'transform 0.3s ease, width 0.3s ease';
        }
      }

      static setTabState(tab, active) {
        tab.setAttribute('aria-selected', active);
      }
    }
  );
}

/* -----------------------------
   Marquee content
   ----------------------------- */

if (!customElements.get("marquee-content")) {
  class Marquee extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.init();
    }
    calculationPaddingSection() {
      (this.heightSection = this.offsetHeight),
        (this.heightSection =
          this.heightSection < 18
            ? 18
            : 96 < this.heightSection
              ? 96
              : this.heightSection),
        this.closest(".js-running-content").style.setProperty(
          "--spacing-padding-block",
          this.heightSection + "px"
        );
    }
    init() {
      (this.distance =
        this.querySelector(".js-marquee-item").offsetWidth),
        (this.speed = this.dataset.speed),
        this.style.setProperty(
          "--marquee-duration",
          this.distance / this.speed + "s"
        );
    }
  }
  customElements.define("marquee-content", Marquee);
}

/* -----------------------------
   VariantSelects
   ----------------------------- */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(EVENT_TYPES.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}
customElements.define('variant-selects', VariantSelects);




/* -----------------------------
   Modal dialog
   ----------------------------- */


class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      this.onButtonClick(button);
    });
  }

  onButtonClick(button) {
    const holder = document.querySelector(`[data-modal="${this.dataset.modal.replace('#', '')}"]`);
    if (!holder) return;

    const template = holder.querySelector('template');
    if (!template) return;

    const modal = template.content.firstElementChild.cloneNode(true);
    document.body.appendChild(modal);

    modal.show(button);
  }
}

customElements.define('modal-opener', ModalOpener);



class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('[id^="ModalClose-"]').forEach(btn => {
      btn.addEventListener('click', () => this.hide());
    });

    this.addEventListener('keyup', e => {
      if (e.code?.toUpperCase() === 'ESCAPE') this.hide();
    });

    this.addEventListener('click', e => {
      if (e.target.classList.contains('modal-dialog__overlay')) {
        this.hide();
      }
    });
  }

  show(opener) {
    this.openedBy = opener;

    requestAnimationFrame(() => {
      this.setAttribute('open', '');
    });

    if (window.pauseAllMedia) window.pauseAllMedia();
  }

  hide() {
    if (this.closing) return;
    this.closing = true;

    this.removeAttribute('open');

    const onEnd = (e) => {
      if (e.target !== this) return;
      if (e.propertyName !== 'opacity') return;

      this.removeEventListener('transitionend', onEnd);
      this.remove();
    };

    this.addEventListener('transitionend', onEnd);

    if (window.pauseAllMedia) window.pauseAllMedia();
    if (typeof resumeMedia !== 'undefined') resumeMedia();

    document.body.dispatchEvent(new CustomEvent('modalClosed'));

  }
}

customElements.define('modal-dialog', ModalDialog);





/* -----------------------------
   Quick View Modal
   ----------------------------- */


class quickviewOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      this.onButtonClick(button);
    });
  }

  onButtonClick(button) {
    const holder = document.querySelector(`[data-modal="${this.dataset.modal.replace('#', '')}"]`);
    if (!holder) return;

    const template = holder.querySelector('template');
    if (!template) return;

    const modal = template.content.firstElementChild.cloneNode(true);
    document.body.appendChild(modal);

    modal.show(button);
  }
}

customElements.define('quickview-opener', quickviewOpener);



class quickAddModal extends HTMLElement {
  constructor() {
    super();
    this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    this.querySelectorAll('[id^="ModalClose-"]').forEach(btn => {
      btn.addEventListener('click', () => this.hide());
    });

    this.addEventListener('keyup', e => {
      if (e.code?.toUpperCase() === 'ESCAPE') this.hide();
    });

    this.addEventListener('click', e => {
      if (e.target.classList.contains('modal-dialog__overlay')) {
        this.hide();
      }
    });
  }

  show(opener) {

     if (window.pauseAllMedia) window.pauseAllMedia();
    opener.setAttribute('aria-disabled', true)


    if (opener.querySelector('.loading-overlay')) {
      opener.querySelector('.loading-overlay').classList.remove('hidden');
    }

    fetch(opener.getAttribute('data-product-url'))
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(
          responseText,
          'text/html'
        )
        this.productElement = responseHTML.querySelector(
          'main-product[id^="MainProduct-"] .product-container'
        )
        this.preventDuplicatedIDs()
        this.removeDOMElements()
        this.setInnerHTML(
          this.modalContent,
          this.productElement.innerHTML,
          opener
        )

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init()
        }

        if (window.ProductModel) window.ProductModel.loadShopifyXR()
 

        this.openedBy = opener;
        requestAnimationFrame(() => {
          this.setAttribute('open', '');

          setTimeout(() => {
            const videos = this.querySelectorAll('video');
            videos.forEach(video => {
              video.muted = true; // autoplay safety
              video.play?.().catch(() => {});
            });
          }, 120);


        });



      })
      .finally(() => {
        opener.removeAttribute('aria-disabled');

        if (opener.querySelector('.loading-overlay')) {
          opener.querySelector('.loading-overlay').classList.add('hidden');
        }


      })




    
  }


  removeDOMElements() {

    const product_inventory = this.productElement.querySelector('product-inventory')
    if (product_inventory) product_inventory.remove();


    const size_guide = this.productElement.querySelector('.size-guide-button')
    if (size_guide) size_guide.remove();

    const icon_with_text = this.productElement.querySelectorAll('.icon-with-text__list')
    icon_with_text.forEach((el) => {
      el.remove()
    });
    const accordion = this.productElement.querySelectorAll('.accordion')
    accordion.forEach((el) => {
      el.remove()
    });
    const additional_products = this.productElement.querySelectorAll('.additional-products')
    additional_products.forEach((el) => {
      el.remove()
    });
    const product_details_meta = this.productElement.querySelectorAll('.product-details-meta-block')
    product_details_meta.forEach((el) => {
      el.remove()
    });

    const customLiquid =
      this.productElement.querySelectorAll('.custom-liquid')
    customLiquid.forEach((el) => {
      el.remove()
    });



  }
  preventDuplicatedIDs() {
    const el = this.productElement
    const sectionId = this.productElement.dataset.section;
  el.querySelectorAll('[id]').forEach(node => {
    node.id = `${node.id}-${sectionId}`;
  });

 el.querySelectorAll('input[type="radio"]').forEach((input) => {
      // make name unique (important for variant switching)
      input.name = `${input.name}-${sectionId}`

      // optional tracking
      input.dataset.originalSection = sectionId

      // fix ID if exists
      if (input.id) {
        input.id = `${input.id}-${sectionId}`
      }
    })

   
    el.querySelectorAll('label').forEach((label) => {
      if (label.htmlFor) {
        label.htmlFor = `${label.htmlFor}-${sectionId}`
      }
    })

 
    el.querySelectorAll('[data-option-id]').forEach((item) => {
      item.dataset.optionId = `${item.dataset.optionId}-${sectionId}`
    })

   
    const form = el.querySelector('form')
    if (form) {
      form.id = form.id
        ? `${form.id}-${sectionId}`
        : `product-form-${sectionId}`
    }

 
    el.querySelectorAll('[id]').forEach((node) => {
      node.id = `${node.id}-${sectionId}`
    })



  }



  setInnerHTML(element, html, opener) {
    element.innerHTML = html

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script')
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      })
      newScriptTag.appendChild(
        document.createTextNode(oldScriptTag.innerHTML)
      )
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
    })

  }


  hide() {
    if (this.closing) return;
    this.closing = true;

    this.removeAttribute('open');

    const onEnd = (e) => {
      if (e.target !== this) return;
      if (e.propertyName !== 'opacity') return;

      this.removeEventListener('transitionend', onEnd);
      this.remove();
    };

    this.addEventListener('transitionend', onEnd);

      setTimeout(() => {
      if (window.pauseAllMedia) window.pauseAllMedia();

      if (typeof resumeMedia !== 'undefined') {
        resumeMedia();
      }
    }, 150);

    document.body.dispatchEvent(new CustomEvent('modalClosed'));

  }
}

customElements.define('quick-add-modal', quickAddModal);

/* -----------------------------
   ColorSwatch
   ----------------------------- */

class ColorSwatch extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {

    this.cached = {};
    this.option = this.querySelector(".color-swatch__radio");
    this.productHandle = this.option.dataset.productHandle;
    this.collectionHandle = this.option.dataset.collectionHandle;
    this.mediaId = this.option.dataset.mediaId;
    this.productCard = this.closest(".product-card") || this.closest(".product-card-content-body");
    this.options = this.productCard?.querySelectorAll(".color-swatch__radio");
    this.form = this.productCard?.querySelector(".js-product-form");

    this.updateSwatchAvailability();
    this.querySelector('a')?.addEventListener('click', (event) => event.preventDefault());
    this.option.addEventListener("change", this.onSwatchChange.bind(this));

    // this.updateMediaSlider(this.mediaId);
  }


  getSelectedVariantId(productCard) {
    const selectedColor = productCard.querySelector('.color-swatch__radio:checked')?.value;
    const selectedSize = productCard.querySelector('.size-input__radio:checked')?.value;
    if (!selectedColor || !selectedSize) return null;

    const variants = JSON.parse(productCard.querySelector('[type="application/json"][data-variants]').textContent);

    const variant = variants.find(v =>
      v.option1 === selectedColor && v.option2 === selectedSize
    );

    return variant ? variant.id : null;
  }



  updateSwatchAvailability() {
    // If the variant is disabled in HTML, leave disabled
    if (!this.option.disabled) {
      this.classList.remove("sold-out");
    } else {
      this.classList.add("sold-out");
    }
  }


  onSwatchChange() {

    this.options.forEach(opt => {
      if (opt !== this.option) {
        opt.checked = false;
        opt.removeAttribute("checked");
      }
    });

    this.option.checked = true;
    this.option.setAttribute("checked", "");

    const selectedVariantId = this.getSelectedVariantId(this.productCard);
    if (!selectedVariantId) return;


    this.variantId = selectedVariantId;


    let sectionUrl = `${theme.routes.root_url}/products/${this.productHandle}?variant=${this.variantId}&view=card`;

    if (this.collectionHandle.length > 0) {
      sectionUrl = `${theme.routes.root_url}/collections/${this.collectionHandle}/products/${this.productHandle}?variant=${this.variantId}&view=card`;
    }



    sectionUrl = sectionUrl.replace('//', '/');

    if (this.cached[sectionUrl]) {
      this.renderProductInfo(this.cached[sectionUrl]);
      return;
    }


    fetch(sectionUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        this.cached[sectionUrl] = html;
        this.renderProductInfo(html);

      })
      .catch((e) => {
        console.error(e);
      });
  }

  renderProductInfo(html) {
    this.form.querySelector('input[name="id"]').value = this.variantId;
    this.updatePrice(html);
    this.updateMedia();
    this.updateMediaSlider(this.mediaId);
    this.cartButton(html);
    this.cardBadge(html);
    this.selectedLebel(html);
    this.updateSwatchAvailability();
  }


  selectedLebel(html) {
    const selector = '.selected-color';
    const destination = this.productCard.querySelector(selector);
    const source = html.querySelector(selector);
    if (source && destination) {
      // Update button content
      destination.innerHTML = source.innerHTML;
    }

  }



  updateMedia() {
    if (this.productCard) {
      this.variantsImages = JSON.parse(this.productCard.querySelector('[type="application/json"][data-variants-images]').textContent);
      const img = this.productCard.querySelector(".product-media [data-primary-image]");
      const variantImage = this.variantsImages.find(v => v.id == this.variantId);
      if (img && variantImage) {
        img.src = variantImage.image.src;
        img.srcset = variantImage.image.srcset;
        img.dataset.src = variantImage.image.src;
        img.dataset.srcset = variantImage.image.srcset;

      }

    }

  }

  updateMediaSlider(mediaId) {
    if (!mediaId) return;
    const swiperEl = this.productCard.querySelector('.product-card__media-slider') || this.productCard.querySelector('.swiper');

    const hasSwiper = swiperEl && swiperEl.swiper;

    let targetIndex = -1;
    let targetSlide = null;
    if (swiperEl) {
      const slides = swiperEl.querySelectorAll('.swiper-slide');
      slides.forEach((slide, index) => {
        const slideMediaId = slide.getAttribute('data-media-id');
        if (String(slideMediaId) === String(mediaId)) {
          targetIndex = index;
          targetSlide = slide;
        }
      });
      slides.forEach(slide => slide.classList.remove('swiper-slide-active'));
      if (targetSlide) targetSlide.classList.add('swiper-slide-active');
    }

    if (hasSwiper && targetIndex >= 0) {
      swiperEl.swiper.slideTo(targetIndex, 300);
    } else if (targetSlide) {
      targetSlide.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

  }


  updatePrice(html) {
    const selector = '.price-container';
    const destination = this.productCard.querySelector(selector);
    const source = html.querySelector(selector);
    if (source && destination) destination.innerHTML = source.innerHTML;
  }
  cardBadge(html) {
    const selector = '.product-badge-container';
    const destination = this.productCard.querySelector(selector);
    const source = html.querySelector(selector);
    if (source && destination) destination.innerHTML = source.innerHTML;
  }


  cartButton(html) {
    const selector = '.button-with-icon';
    const selector_icon_only = '.button-icon-only';
    this.cartButtonChanger(html, selector);
    this.cartButtonChanger(html, selector_icon_only);
  }

  cartButtonChanger(html, selector) {
    const destination = this.productCard.querySelector(selector);
    const source = html.querySelector(selector);

    if (source && destination) {
      // Update button content
      destination.innerHTML = source.innerHTML;

      // Sync disabled / aria-disabled
      if (source.disabled) {
        destination.disabled = true;
        destination.querySelector(".sold-out")?.classList.remove("hidden");
      } else {
        destination.disabled = false;
        destination.querySelector(".sold-out")?.classList.add("hidden");
      }
    }

  }

}
customElements.define("color-swatch", ColorSwatch);

/* -----------------------------
   SizeVariant
   ----------------------------- */

class SizeVariant extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {

    this.cached = {};
    this.option = this.querySelector(".size-input__radio");
    this.productHandle = this.option.dataset.productHandle;
    this.collectionHandle = this.option.dataset.collectionHandle;
    this.mediaId = this.option.dataset.mediaId;
    this.productCard = this.closest(".product-card") || this.closest(".product-card-content-body");
    this.options = this.productCard?.querySelectorAll(".size-input__radio");
    this.form = this.productCard?.querySelector(".js-product-form");
    this.updateSwatchAvailability();
    this.option.addEventListener("change", this.onSwatchChange.bind(this));

  }

  updateSwatchAvailability() {
    // If the variant is disabled in HTML, leave disabled
    if (!this.option.disabled) {
      this.classList.remove("sold-out");
    } else {
      this.classList.add("sold-out");
    }
  }
  getSelectedVariantId(productCard) {
    const selectedColor = productCard.querySelector('.color-swatch__radio:checked')?.value;
    const selectedSize = productCard.querySelector('.size-input__radio:checked')?.value;
    if (!selectedColor || !selectedSize) return null;
    const variants = JSON.parse(productCard.querySelector('[type="application/json"][data-variants]').textContent);
    const variant = variants.find(v =>
      v.option1 === selectedColor && v.option2 === selectedSize
    );
    return variant ? variant.id : null;
  }

  onSwatchChange() {

    this.options.forEach(opt => {
      if (opt !== this.option) {
        opt.checked = false;
        opt.removeAttribute("checked");
      }
    });

    this.option.checked = true;
    this.option.setAttribute("checked", "");

    const selectedVariantId = this.getSelectedVariantId(this.productCard);
    if (!selectedVariantId) return;
    this.variantId = selectedVariantId;

    let sectionUrl = `${theme.routes.root_url}/products/${this.productHandle}?variant=${this.variantId}&view=card`;

    if (this.collectionHandle.length > 0) {
      sectionUrl = `${theme.routes.root_url}/collections/${this.collectionHandle}/products/${this.productHandle}?variant=${this.variantId}&view=card`;
    }


    sectionUrl = sectionUrl.replace('//', '/');
    if (this.cached[sectionUrl]) {
      this.renderProductInfo(this.cached[sectionUrl]);
      return;
    }


    fetch(sectionUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        this.cached[sectionUrl] = html;
        this.renderProductInfo(html);

      })
      .catch((e) => {
        console.error(e);
      });
  }

  renderProductInfo(html) {
    this.form.querySelector('input[name="id"]').value = this.variantId;
    this.selectedLebel(html);
    this.cartButton(html);
    this.updateSwatchAvailability();
  }



  selectedLebel(html) {
    const selector = '.selected-size';

    const destination = this.productCard.querySelector(selector);
    const source = html.querySelector(selector);

    if (source && destination) {
      // Update button content
      destination.innerHTML = source.innerHTML;
    }

  }

  cartButton(html) {
    const selector = '.button-with-icon';
    const selector_icon_only = '.button-icon-only';
    this.cartButtonChanger(html, selector);
    this.cartButtonChanger(html, selector_icon_only);
  }

  cartButtonChanger(html, selector) {
    const destination = this.productCard.querySelector(selector);
    const source = html.querySelector(selector);

    if (source && destination) {
      // Update button content
      destination.innerHTML = source.innerHTML;

      // Sync disabled / aria-disabled
      if (source.disabled) {
        destination.disabled = true;
        destination.querySelector(".sold-out")?.classList.remove("hidden");
      } else {
        destination.disabled = false;
        destination.querySelector(".sold-out")?.classList.add("hidden");
      }
    }

  }




}
customElements.define("size-variant", SizeVariant);


/* -----------------------------
   CountdownTimer
   ----------------------------- */

class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.interval = null;
  }

  connectedCallback() {
    this.render();
    this.startTimer();
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  parseEndTimeUTCMinus4(str) {
    const date = new Date(str); // parse as local time
    const localOffset = date.getTimezoneOffset(); // minutes
    const targetOffset = 4 * 60; // 240 minutes for UTC-4
    return date.getTime() + (localOffset + targetOffset) * 60 * 1000;
  }

  startTimer() {
    const endDate = this.dataset.endDate;
    if (!endDate) return;

    const endTime = this.parseEndTimeUTCMinus4(endDate);

    this.interval = setInterval(() => {
      const now = Date.now();
      let timeLeft = Math.floor((endTime - now) / 1000);

      if (timeLeft <= 0) {
        clearInterval(this.interval);
        this.innerHTML = `<span class="expired">00<span>Days</span></span>
                          <span class="count-divider">:</span>
                          <span class="expired">00<span>Hrs</span></span>
                          <span class="count-divider">:</span>
                          <span class="expired">00<span>Min</span></span>
                          <span class="count-divider">:</span>
                          <span class="expired">00<span>Sec</span></span>`;
        return;
      }

      const days = String(Math.floor(timeLeft / 86400)).padStart(2, '0');
      timeLeft %= 86400;
      const hours = String(Math.floor(timeLeft / 3600)).padStart(2, '0');
      timeLeft %= 3600;
      const minutes = String(Math.floor(timeLeft / 60)).padStart(2, '0');
      const seconds = String(timeLeft % 60).padStart(2, '0');

      this.innerHTML = `
        <span>${days}<span class="count-text">Days</span></span>
         <span class="count-divider">:</span>
        <span>${hours}<span class="count-text">Hrs</span></span>
         <span class="count-divider">:</span>
        <span>${minutes}<span class="count-text">Min</span></span>
         <span class="count-divider">:</span>
        <span>${seconds}<span class="count-text">Sec</span></span>
      `;
    }, 1000);
  }

  render() {
    this.style.display = 'inline-flex';
    this.innerHTML = `Loading...`;
  }
}

customElements.define('countdown-timer', CountdownTimer);




/* -----------------------------
   BeforeAfterImages
   ----------------------------- */

class BeforeAfterImages extends HTMLElement {
  constructor() {
    super(),
      (this.$ = this.querySelector.bind(this)),
      (this.$$ = this.querySelectorAll.bind(this)),
      this.init();
  }

  connectedCallback() {
    this.observeResize();
    this.animateToCenter(); // Run animation on load
  }

  observeResize() {
    (this.resizeObserver = new ResizeObserver(() => this.init())),
      this.resizeObserver.observe(this.afterImage);
  }

  init() {
    this.afterImage = this.$(".js-after-image");
    this.splitCusor = this.$(".js-split-cursor");
    this.imageWith = this.afterImage.offsetWidth;
    this.imageHeight = this.afterImage.offsetHeight;
    this.isClicked = false;
    this.handleEvent();
  }

  handleEvent() {
    this.splitCusor.addEventListener("mousedown", this.slideReady.bind(this), { passive: true });
    this.splitCusor.addEventListener("touchstart", this.slideReady.bind(this), { passive: true });
    this.splitCusor.addEventListener("mouseup", this.slideFinish.bind(this), { passive: true });
    this.splitCusor.addEventListener("touchend", this.slideFinish.bind(this), { passive: true });
  }

  slideReady() {
    this.isClicked = true;
    document.addEventListener("mousemove", this.slideMoveBind = this.slideMove.bind(this));
    document.addEventListener("touchmove", this.slideMoveBind);
  }

  slideFinish() {
    this.isClicked = false;
    document.removeEventListener("mousemove", this.slideMoveBind);
    document.removeEventListener("touchmove", this.slideMoveBind);
  }

  slideMove(t) {
    if (this.isClicked) {
      const e = this.offsetLeft;
      t = (t.changedTouches ? t.changedTouches[0] : t).clientX - e;
      this.posSplitCusor = (t / this.imageWith) * 100;
      this.posSplitCusor = Math.min(100, Math.max(0, this.posSplitCusor));
      requestAnimationFrame(() => {
        this.style.setProperty("--before-after-initial-drag-position", this.posSplitCusor + "%");
      });
    }
  }

  animateToCenter() {
    // Start from 0%
    this.style.setProperty("--before-after-initial-drag-position", "0%");
    // Smoothly move to 50% after a short delay
    requestAnimationFrame(() => {
      this.style.transition = "--before-after-initial-drag-position 1.5s ease-in-out";
      this.style.setProperty("--before-after-initial-drag-position", "50%");
      // Remove transition after animation so manual dragging is instant
      setTimeout(() => {
        this.style.transition = "";
      }, 1600);
    });
  }
}

customElements.define("before-after-images", BeforeAfterImages);






class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer__inner = this.querySelector(".menu-drawer__inner");
    this.closeBtn = this.querySelector(".button-close");
    this.drawerOverlay = this.querySelector(".menu-drawer__overlay");
    this.isOpen = false;
  }

  connectedCallback() {
    this.initDrawer();
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target.contains(this)) {
        this.openDrawer();
      }
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target.contains(this)) {
        this.closeDrawer();
      }
    });

    if (Shopify.designMode) {
      this.initDrawer();
    }

  }

  initDrawer() {

    if (!this.drawer__inner) return;

    if (typeof gsap !== "undefined") {
      gsap.set(this.drawer__inner, { xPercent: -110, display: "none" });
    }

    this.closeBtn?.addEventListener("click", () => this.closeDrawer());
    this.drawerOverlay?.addEventListener("click", () => this.closeDrawer());


    this.querySelectorAll(".menu-drawer__items, .menu-drawer__submenu--item").forEach(
      (item) => {
        item.addEventListener("click", (e) => {
          if (e.target.closest("a")) return;
          e.preventDefault();
          const submenuId = item.getAttribute("data-target");
          const submenu = this.querySelector(`[data-submenu='${submenuId}']`);
          if (submenu) this.openSubMenu(submenu);
        });
      }
    );

    this.querySelectorAll(".submenu-level__back").forEach((backBtn) => {
      backBtn.addEventListener("click", (e) => {
        e.preventDefault();
        const submenu = backBtn.closest(".menu-drawer__submenu");
        if (submenu && typeof gsap !== "undefined") {
          gsap.to(submenu, {
            xPercent: -110,
            duration: 0.7,
            onComplete: () => {
              submenu.setAttribute("hidden", "");
              submenu.removeAttribute("inert");
            },
          });
        }
      });
    });
  }

  openDrawer() {
    if (typeof gsap === "undefined" || this.isOpen) return;
    this.isOpen = true;
    const mobile_menu = this.querySelector(".menu-drawer");

    setTimeout(() => {
      document.body.classList.add("overflow-hidden", "open-menu-drawer");

      if (this.drawerOverlay) {
        this.drawerOverlay.classList.add("active");
        this.drawerOverlay.setAttribute("aria-hidden", "false");
      }

    }, 250);

    this.drawer__inner.style.display = "flex";
    if (mobile_menu) mobile_menu.setAttribute("aria-hidden", "false");
    const headerMenu = this.drawer__inner.querySelector(".menu-drawer__header");
    const menuItems = this.drawer__inner.querySelectorAll(".menu-drawer__list > li");
    const footer = this.drawer__inner.querySelector(".menu-drawer__footer");

    const tl = gsap.timeline();

    tl.to(this.drawer__inner, {
      display: "flex",
      delay: 0.2,
    });

    tl.to(this.drawer__inner, {
      xPercent: 0,
      duration: 0.3,
      ease: "power4.out",
    });

    if (headerMenu) {
      tl.from(headerMenu, {
        y: -100,
        duration: 0.4,
        opacity: 0,
        ease: "back.out(1.7)",
      });
    }

    if (menuItems.length) {
      tl.fromTo(
        menuItems,
        { xPercent: -100, opacity: 0 },
        {
          duration: 0.5,
          opacity: 1,
          xPercent: 0,
          stagger: 0.1,
          ease: "power4.out",
        }
      );
    }

    if (footer) {
      tl.from(footer, {
        y: 100,
        duration: 0.3,
        ease: "back.out(1.7)",
        opacity: 0,
      });
    }
  }

  closeDrawer() {
    if (typeof gsap === "undefined" || !this.isOpen) return;

    this.isOpen = false;
    if (document.activeElement) {
      document.activeElement.blur();
    }
    const mobile_menu = this.querySelector(".menu-drawer");
    const tl = gsap.timeline();

    const toggler = document.querySelector("nav-toggler");
    if (toggler) toggler.focus();

    tl.to(this.drawer__inner, {
      xPercent: -110,
      duration: 0.6,
      ease: "power4.in",
    }).add(() => {
      this.drawer__inner.style.display = "none";
      document.body.classList.remove("overflow-hidden", "open-menu-drawer");
      if (this.drawerOverlay) this.drawerOverlay.classList.remove("active");
      if (mobile_menu) mobile_menu.setAttribute("aria-hidden", true);

      this.querySelectorAll(".menu-drawer__submenu").forEach((sub) => {
        gsap.set(sub, { xPercent: -110 });
        sub.setAttribute("hidden", "");
        sub.setAttribute("inert", "");
      });


    });


  }

  openSubMenu(submenu) {
    if (typeof gsap === "undefined") return;

    gsap.set(submenu, { display: "block" });
    submenu.removeAttribute("hidden");
    submenu.removeAttribute("inert");
    gsap.fromTo(
      submenu,
      { xPercent: -110, opacity: 1 },
      { xPercent: 0, opacity: 1, duration: 0.7 }
    );

    const focusTarget = submenu.querySelector(
      "button, a, input, select, textarea, [tabindex]:not([tabindex='-1'])"
    );
    if (focusTarget) focusTarget.focus();
  }
}

customElements.define("menu-drawer", MenuDrawer);


class NavToggler extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", () => {
      const drawer = document.querySelector("menu-drawer");
      if (drawer && typeof drawer.openDrawer === "function") {
        drawer.openDrawer();
      }
    });
  }
}
customElements.define("nav-toggler", NavToggler);



class SearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.searchClose = this.querySelector(".button-close");
    this.drawerOverlay = this.querySelector(".search-drawer__overlay");
    this.anime = this.querySelectorAll(".anim-up");
    this.isOpen = false;
    this.tl = null;
  }

  connectedCallback() {
    this.searchClose?.addEventListener("click", () => this.closeDrawer());
    this.drawerOverlay?.addEventListener("click", () => this.closeDrawer());

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target.contains(this)) {
          this.openDrawer();
        }
      });

      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target.contains(this)) {
          this.closeDrawer();
        }
      });
    }
  }

  openDrawer() {
    if (typeof gsap === "undefined" || this.isOpen) return;

    this.isOpen = true;
    document.body.classList.add("overflow-hidden");
    this.classList.add("open-drawer");

    if (!this.tl) {
      this.tl = gsap.timeline({ paused: true });

      if (this.anime.length) {
        this.tl.fromTo(
          this.anime,
          { xPercent: -100, opacity: 0 },
          {
            duration: 0.7,
            opacity: 1,
            xPercent: 0,
            stagger: 0.18,
            ease: "power4.out",
          }
        );
      }
    }

    this.tl.play();
    if (this.drawerOverlay) {
      this.drawerOverlay.classList.add("active");
      this.drawerOverlay.setAttribute("aria-hidden", "false");
    }
  }

  closeDrawer() {
    if (!this.isOpen) return;

    this.isOpen = false;
    document.activeElement?.blur();

    if (this.tl) {
      this.tl.reverse();
    }
    gsap.delayedCall(0.7, () => {
      document.body.classList.remove("overflow-hidden");
      this.classList.remove("open-drawer");

      if (this.drawerOverlay) {
        this.drawerOverlay.classList.remove("active");
        this.drawerOverlay.setAttribute("aria-hidden", "true");
      }
    });


  }
}

customElements.define("search-drawer", SearchDrawer);


class searchOpener extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (e) => {
      e.preventDefault();
      const searchDrawer = document.querySelector("search-drawer");
      if (searchDrawer && typeof searchDrawer.openDrawer === "function") {
        searchDrawer.openDrawer();
      }
    });
  }
}
customElements.define("search-opener", searchOpener);





if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.custom-select__btn');
      this.listbox = this.querySelector('.custom-select__listbox');
      this.selectedOption = this.querySelector('[aria-selected="true"]');

      // Set the selected option.
      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }

      this.setButtonWidth();
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.options = this.querySelectorAll('.custom-select__option');
      this.nativeSelect = document.getElementById(`${this.id}-native`);
      this.swatches = !!Array.from(this.options).find((el) => 'swatch' in el.dataset);
      this.focusedClass = 'is-focused';
      this.searchString = '';
      this.listboxOpen = false;
      this.selectedOption = this.querySelector('[aria-selected="true"]');

      // Set the selected option.
      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }

      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.button.addEventListener('mousedown', this.handleMousedown.bind(this));
    }

    /**
     * Returns the next visible sibling element
     * @param {Element} elem - The element to search
     * @returns {Element}
     */
    static getNextVisibleSibling(elem) {
      let sibling = elem.nextElementSibling;
      while (sibling) {
        if (sibling.offsetParent) return sibling;
        sibling = sibling.nextElementSibling;
      }
      return null;
    }

    /**
     * Returns the previous visible sibling element
     * @param {Element} elem - The element to search
     * @returns {Element}
     */
    static getPreviousVisibleSibling(elem) {
      let sibling = elem.previousElementSibling;
      while (sibling) {
        if (sibling.offsetParent) return sibling;
        sibling = sibling.previousElementSibling;
      }
      return null;
    }

    /**
     * Adds event listeners when the options list is open.
     */
    addListboxOpenListeners() {
      this.mouseoverHandler = this.handleMouseover.bind(this);
      this.mouseleaveHandler = this.handleMouseleave.bind(this);
      this.clickHandler = this.handleClick.bind(this);
      this.blurHandler = this.handleBlur.bind(this);

      this.listbox.addEventListener('mouseover', this.mouseoverHandler);
      this.listbox.addEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.addEventListener('click', this.clickHandler);
      this.listbox.addEventListener('blur', this.blurHandler);
    }

    /**
     * Removes event listeners added when the options list was open.
     */
    removeListboxOpenListeners() {
      this.listbox.removeEventListener('mouseover', this.mouseoverHandler);
      this.listbox.removeEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.removeEventListener('click', this.clickHandler);
      this.listbox.removeEventListener('blur', this.blurHandler);
    }

    /**
     * Handles 'keydown' events on the custom select element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      if (this.listboxOpen) {
        this.handleKeyboardNav(evt);
      } else if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown' || evt.key === ' ') {
        evt.preventDefault();
        this.showListbox();
      }
    }

    /**
     * Handles 'mousedown' events on the button element.
     * @param {object} evt - Event object.
     */
    handleMousedown(evt) {

      if (!this.listboxOpen && evt.button === 0) {

        this.showListbox();
      }
    }

    /**
     * Handles 'mouseover' events on the options list.
     * @param {object} evt - Event object.
     */
    handleMouseover(evt) {
      if (evt.target.matches('li')) {
        this.focusOption(evt.target);
      }
    }

    /**
     * Handles 'mouseleave' events on the options list.
     */
    handleMouseleave() {
      this.focusOption(this.selectedOption);
    }

    /**
     * Handles 'click' events on the custom select element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      // mouse down + move + up will trigger a click event on parent - only respond to clicks on LI
      if (evt.target.tagName === 'LI') {
        this.selectOption(evt.target);
      }
    }

    /**
     * Handles 'blur' events on the options list.
     */
    handleBlur() {
      if (this.listboxOpen) {
        this.hideListbox();
      }
    }

    /**
     * Handles 'keydown' events on the options list.
     * @param {object} evt - Event object.
     */
    handleKeyboardNav(evt) {
      let optionToFocus;

      // Disable tabbing if options list is open (as per native select element).
      if (evt.key === 'Tab') {
        evt.preventDefault();
      }

      switch (evt.key) {
        // Focus an option.
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (evt.key === 'ArrowUp') {
            optionToFocus = CustomSelect.getPreviousVisibleSibling(this.focusedOption);
          } else {
            optionToFocus = CustomSelect.getNextVisibleSibling(this.focusedOption);
          }

          if (optionToFocus && !optionToFocus.classList.contains('is-disabled')) {
            this.focusOption(optionToFocus);
          }
          break;

        // Select an option.
        case 'Enter':
        case ' ':
          evt.preventDefault();
          this.selectOption(this.focusedOption);
          break;

        // Cancel and close the options list.
        case 'Escape':
          evt.preventDefault();
          this.hideListbox();
          break;

        // Search for an option and focus the first match (if one exists).
        default:
          optionToFocus = this.findOption(evt.key);

          if (optionToFocus) {
            this.focusOption(optionToFocus);
          }
          break;
      }
    }

    /**
     * Sets the button width to the same as the longest option, to prevent
     * the button width from changing depending on the option selected.
     */
    setButtonWidth() {
      // Get the width of an element without side padding.
      const getHorizontalPadding = (el) => {
        const elStyle = getComputedStyle(el);
        return parseFloat(elStyle.paddingLeft) + parseFloat(elStyle.paddingRight);
      };

      const buttonPadding = getHorizontalPadding(this.button);
      const optionPadding = getHorizontalPadding(this.selectedOption);
      const buttonBorder = this.button.offsetWidth - this.button.clientWidth;
      const optionWidth = Math.ceil(this.selectedOption.getBoundingClientRect().width);

      this.button.style.setProperty('--custom-select-button-width', `${optionWidth - optionPadding + buttonPadding + buttonBorder}px`);
    }

    /**
     * Shows the options list.
     */
    showListbox() {
      // this.listbox.hidden = false;
      this.listboxOpen = true;

      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.listbox.setAttribute('aria-hidden', 'false');


      // Slight delay required to prevent blur event being fired immediately.
      setTimeout(() => {
        this.focusOption(this.selectedOption);
        this.listbox.focus();
        this.addListboxOpenListeners();
      });
    }

    /**
     * Hides the options list.
     */
    hideListbox() {
      if (!this.listboxOpen) return;

      // this.listbox.hidden = true;
      this.listboxOpen = false;

      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      this.listbox.setAttribute('aria-hidden', 'true');

      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
        this.focusedOption = null;
      }

      this.button.focus();
      this.removeListboxOpenListeners();
    }

    /**
     * Finds a matching option from a typed string.
     * @param {string} key - Key pressed.
     * @returns {?Element}
     */
    findOption(key) {
      this.searchString += key;

      // If there's a timer already running, clear it.
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      // Wait 500ms to see if another key is pressed, if not then clear the search string.
      this.searchTimer = setTimeout(() => {
        this.searchString = '';
      }, 500);

      // Find an option that contains the search string (if there is one).
      const matchingOption = Array.from(this.options).find((option) => {
        const label = option.innerText.toLowerCase();
        return label.includes(this.searchString) && !option.classList.contains('is-disabled');
      });

      return matchingOption;
    }

    /**
     * Focuses an option.
     * @param {Element} option - <li> element of the option to focus.
     */
    focusOption(option) {
      // Remove focus on currently focused option (if there is one).
      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
      }

      // Set focus on the option.
      this.focusedOption = option;
      this.focusedOption.classList.add(this.focusedClass);

      // If option is out of view, scroll the list.
      if (this.listbox.scrollHeight > this.listbox.clientHeight) {
        const scrollBottom = this.listbox.clientHeight + this.listbox.scrollTop;
        const optionBottom = option.offsetTop + option.offsetHeight;

        if (optionBottom > scrollBottom) {
          this.listbox.scrollTop = optionBottom - this.listbox.clientHeight;
        } else if (option.offsetTop < this.listbox.scrollTop) {
          this.listbox.scrollTop = option.offsetTop;
        }
      }
    }

    /**
     * Selects an option.
     * @param {Element} option - Option <li> element.
     */
    selectOption(option) {
      if (option !== this.selectedOption) {
        // Switch aria-selected attribute to selected option.
        option.setAttribute('aria-selected', 'true');
        this.selectedOption.setAttribute('aria-selected', 'false');

        // Update swatch colour in the button.
        if (this.swatches) {
          this.button.dataset.swatch = option.dataset.swatch || '';
          const nativeSwatchColor = option.style.getPropertyValue('--native-swatch-color');
          if (nativeSwatchColor) {
            this.button.style.setProperty('--native-swatch-color', nativeSwatchColor);
            this.button.style.setProperty('--native-swatch-image', '');
          }
          const nativeSwatchImage = option.style.getPropertyValue('--native-swatch-image');
          if (nativeSwatchImage) {
            this.button.style.setProperty('--native-swatch-image', nativeSwatchImage);
            this.button.style.setProperty('--native-swatch-color', '');
          }
        }

        // Update the button text and set the option as active.
        this.button.firstElementChild.textContent = option.firstElementChild.textContent;
        this.listbox.setAttribute('aria-activedescendant', option.id);
        this.selectedOption = document.getElementById(option.id);

        // If a native <select> exists, update its selected value and dispatch a 'change' event.
        if (this.nativeSelect) {
          this.nativeSelect.value = option.dataset.value;
          this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          // Dispatch a 'change' event on the custom select element.
          const detail = { selectedValue: option.dataset.value };
          this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail }));
        }
      }

      this.hideListbox();
    }
  }

  customElements.define('custom-select', CustomSelect);
}



if (!customElements.get('search-form')) {

  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-search-input');
      this.productTypeInput = document.getElementById('product_type_input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.resetBtn = this.querySelector('.js-search-reset');
    }
    connectedCallback() {
      this.init();
    }

    init() {
      this.input.addEventListener('input', debounce(this.handleInputField.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.resetBtn.addEventListener('click', this.handleResetBtn.bind(this));
      if ((this.input.dataset.placeholderTwo || this.input.dataset.placeholderThree)) {
        this.typeInPlaceholdersChange();
      }
    }

    /**
   * Simulates the placeholder titles being typed in and the previous one deleted, on repeat
   */
    typeInPlaceholdersChange() {
      const typingSpeed = this.input.dataset.typingSpeed || 100;
      const deletingSpeed = this.input.dataset.deletingSpeed || 60;
      const delayAfterDeleting = this.input.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete = this.input.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped = this.input.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.input.placeholder) {
        placeholders.push(this.input.placeholder);
      }

      if (this.input.dataset.placeholderTwo) {
        placeholders.push(this.input.dataset.placeholderTwo);
      }

      if (this.input.dataset.placeholderThree) {
        placeholders.push(this.input.dataset.placeholderThree);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute('placeholder');

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder = (currentPlaceholder.length >= 3
            && placeholder.startsWith(currentPlaceholder))
            ? placeholder.replace(currentPlaceholder, '')
            : placeholder;

          const typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute('placeholder');
            this.input.setAttribute('placeholder', currentPlaceholder + nextPlaceholder.charAt(0));
            if (nextPlaceholder.length === 1) {
              resolve();
              clearInterval(typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute('placeholder');
          const deletionIntervalId = setInterval(() => {
            const newPlaceholder = prevPlaceholder.substring(0, prevPlaceholder.length - 1);
            this.input.setAttribute('placeholder', newPlaceholder);
            prevPlaceholder = newPlaceholder;
            if (prevPlaceholder.length === 0
              || (prevPlaceholder.length >= 3 && nextPlaceholder.startsWith(prevPlaceholder))) {
              resolve();
              clearInterval(deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              setTimeout(showNextPlaceholder.bind(this), delayAfterWordTyped);
            });
          }, delayAfterDeleting);
        });
      };

      setTimeout(showNextPlaceholder.bind(this), delayBeforeFirstDelete);
    }
    /**
    * Handles a change of product type
    * @param {object} evt - Event object.
    */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
    }

    /**
 * Handles 'input' events on the search field.
 */
    handleInputField() {
      if (this.input.value.length > 0) {
        this.input.classList.add('search-input--filled');
      } else {
        this.input.classList.remove('search-input--filled');
      }
    }


    /**
 * Handles the reset button being clicked
 */
    handleResetBtn() {
      this.input.classList.remove('search-input--filled');
    }

  }

  customElements.define('search-form', SearchForm);
}


if (!customElements.get('product-recommendations')) {
  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();

      this.ROOT_MARGIN = '0px 0px 200px 0px';
    }

    connectedCallback() {
      if (this.hasAttribute('data-force-load')) {
        this.loadRecommendations();
      } else {
        this.setupIntersectionObserver();
      }
    }

    loadRecommendations() {
      fetch(this.dataset.url)
        .then(response => response.text())
        .then(text => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector(
            `[data-container="${this.dataset.container}"]`
          );

          if (
            recommendations &&
            recommendations.innerHTML.trim().length
          ) {
            this.innerHTML = recommendations.innerHTML;
          }
        })
        .catch(e => {
          console.error(e);
        });
    }

    setupIntersectionObserver() {
      new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);

          this.loadRecommendations();
        },
        {
          rootMargin: this.ROOT_MARGIN
        }
      ).observe(this);
    }
  }

  customElements.define(
    'product-recommendations',
    ProductRecommendations
  );
}


if (!customElements.get('tabbed-content')) {
  class Tabs extends HTMLElement {
    constructor() {
      super();
      this.tabList = this.querySelector('[role="tablist"]');
      this.activeTab = this.tabList.querySelector('[aria-selected="true"]');
      this.isVerticalTablist =
        this.tabList.getAttribute('aria-orientation') === 'vertical';
      this.tabs = this.querySelectorAll('[role="tab"]');
      this.panels = this.querySelectorAll('[role="tabpanel"]');

      // If no tab is active by default, activate the first tab.
      if (!this.activeTab) {
        this.activeTab = this.tabs[0];
        this.activateTab(this.activeTab);
      }

      this.addListeners();
    }

    addListeners() {
      this.tabList.addEventListener('click', this.handleClick.bind(this));
      this.tabList.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    handleClick(evt) {
      if (!evt.target.matches('[role="tab"]') || evt.target === this.activeTab) return;
      this.activateTab(evt.target);
    }

    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          evt.preventDefault();
          if (!this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();
          if (this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        case 'Home':
          evt.preventDefault();
          this.activateTab(this.tabs[0]);
          break;

        case 'End':
          evt.preventDefault();
          this.activateTab(this.tabs[this.tabs.length - 1]);
          break;
      }
    }

    switchTabOnKeyPress(key) {
      if (key === 'ArrowRight' || key === 'ArrowDown') {
        if (this.activeTab === this.tabs[this.tabs.length - 1]) {
          this.activateTab(this.tabs[0]);
        } else {
          this.activateTab(this.activeTab.nextElementSibling);
        }
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        if (this.activeTab === this.tabs[0]) {
          this.activateTab(this.tabs[this.tabs.length - 1]);
        } else {
          this.activateTab(this.activeTab.previousElementSibling);
        }
      }
    }

    activateTab(tab) {
      this.deactivateActiveTab();

      Tabs.setTabState(tab, true);
      tab.removeAttribute('tabindex');
      this.activeTab = tab;

      if (document.activeElement.matches('.tablist__tab')) {
        tab.focus();
      }
    }

    deactivateActiveTab() {
      if (!this.activeTab) return;

      Tabs.setTabState(this.activeTab, false);
      this.activeTab.setAttribute('tabindex', '-1');
      this.activeTab = null;
    }

    static setTabState(tab, active) {
      tab.setAttribute('aria-selected', active);


      tab.classList.toggle('active', active);

      const panelId = tab.getAttribute('aria-controls');
      const panel = document.getElementById(panelId);


      panel.classList.toggle('active', active);
    }
  }

  customElements.define('tabbed-content', Tabs);
}


class filtersDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer__inner = this.querySelector(".filters-drawer__inner");
    this.closeBtn = this.querySelector(".button-close");
    this.drawerOverlay = this.querySelector(".filters-drawer__overlay");
    this.isOpen = false;
  }

  connectedCallback() {
    this.initDrawer();
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target.contains(this)) {
        this.openDrawer();
      }
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target.contains(this)) {
        this.closeDrawer();
      }
    });

    if (Shopify.designMode) {
      this.initDrawer();
    }

  }

  initDrawer() {

    if (!this.drawer__inner) return;

    if (typeof gsap !== "undefined") {
      gsap.set(this.drawer__inner, { xPercent: -110, display: "none" });
    }

    this.closeBtn?.addEventListener("click", () => this.closeDrawer());
    this.drawerOverlay?.addEventListener("click", () => this.closeDrawer());



  }

  openDrawer() {
    if (typeof gsap === "undefined" || this.isOpen) return;
    this.isOpen = true;
    const filters_drawer = this.querySelector(".filters-drawer");

    setTimeout(() => {
      document.body.classList.add("overflow-hidden", "open-filter-drawer");

      if (this.drawerOverlay) {
        this.drawerOverlay.classList.add("active");
        this.drawerOverlay.setAttribute("aria-hidden", "false");
      }

    }, 250);

    this.drawer__inner.style.display = "flex";

    document.querySelectorAll("accordion-filter").forEach((el) => {
      if (el.details.open) {
        el.panel.style.height = el.panel.scrollHeight + "px";

        requestAnimationFrame(() => {
          el.panel.style.height = "auto";
        });
      }
    });


    if (filters_drawer) filters_drawer.setAttribute("aria-hidden", "false");
    const headerMenu = this.drawer__inner.querySelector(".filters-drawer__header");
    const filterItems = this.drawer__inner.querySelectorAll(".filters-drawer__wrapper .facet__panel ");
    const footer = this.drawer__inner.querySelector(".filters-drawer__footer");

    const tl = gsap.timeline();

    tl.to(this.drawer__inner, {
      display: "flex",
      delay: 0.2,
    });

    tl.to(this.drawer__inner, {
      xPercent: 0,
      duration: 0.3,
      ease: "power4.out",
    });

    if (headerMenu) {
      tl.from(headerMenu, {
        y: -100,
        duration: 0.4,
        opacity: 0,
        ease: "back.out(1.7)",
      });
    }

    if (filterItems.length) {
      tl.fromTo(
        filterItems,
        { xPercent: -100, opacity: 0 },
        {
          duration: 0.5,
          opacity: 1,
          xPercent: 0,
          stagger: 0.1,
          ease: "power4.out",
        }
      );
    }


    if (footer) {
      tl.from(footer, {
        y: 100,
        duration: 0.3,
        ease: "back.out(1.7)",
        opacity: 0,
      });
    }
  }

  closeDrawer() {
    if (typeof gsap === "undefined" || !this.isOpen) return;

    this.isOpen = false;
    if (document.activeElement) {
      document.activeElement.blur();
    }
    const filters_drawer = this.querySelector(".filters-drawer");
    const tl = gsap.timeline();

    const toggler = document.querySelector("filter-toggeler");
    if (toggler) toggler.focus();

    tl.to(this.drawer__inner, {
      xPercent: -110,
      duration: 0.6,
      ease: "power4.in",
    }).add(() => {
      this.drawer__inner.style.display = "none";
      document.body.classList.remove("overflow-hidden", "open-filter-drawer");
      if (this.drawerOverlay) this.drawerOverlay.classList.remove("active");
      if (filters_drawer) filters_drawer.setAttribute("aria-hidden", true);

    });


  }

}

customElements.define("filters-drawer", filtersDrawer);


class facetToggler extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", () => {
      const drawer = document.querySelector("filters-drawer");
      if (drawer && typeof drawer.openDrawer === "function") {
        drawer.openDrawer();
      }
    });
  }
}
customElements.define("facet-toggeler", facetToggler);






 